import { ServiceNpcAnimation } from './service-npc-animation';
import { MeshyNpcAssets } from './meshy-npc-assets';
import { meshyNpcClip, meshyNpcFrame, meshyNpcAnchor } from './meshy-npc-clips';
import { npcWorldFacing } from './npc-presentation';
import { NPCPresentationClock } from './npc-presentation-clock';
import { captureFramePixels, inspectTextureReferences } from './renderer-diagnostics';
import { roadRibbonLayers } from '@/modules/game-procedural/road-paths';
import { RestActivityClock, restActivityAssetKey, restActivityFallback, type RestActivityView } from './rest-activity-clock';
import { RestIdleAssets, restIdleFrame } from './rest-idle-assets';
import { IndividualIdleAssets, individualIdleFrame, individualIdleTextureIndex, resetIndividualIdleAnimations, stableHeroIdleCharacter } from './individual-idle-assets';
import { serviceNpcModel } from './service-npc-appearance';
import { HeroAnimationAssets, wholeAnimationFrame, wholeAnimationPresentation } from './hero-animation-assets';
import { HERO_ANIMATION, type HeroAnimationView } from './hero-animation';
import { MESHY_CAMP_FIRE_OFFSET, meshyClipDuration, meshyClipFrame } from './meshy-hero-clips';
import { Application, Container, Graphics, Matrix, Rectangle, Sprite, Text, TextStyle, Texture } from 'pixi.js';
import { TILE_WIDTH, TILE_HEIGHT, MAP_ZOOM } from '@/shared/constants';
import { getViewport } from '@/shared/telegram';
import type { CameraView, ReadonlyData, RenderSnapshot } from '@/modules/game-domain/render-snapshot';
import type { TownData, NPCData, BuildingData, TownObjectData } from '@/game/types';
import { MAX_SPRITE_PRESENTATION_EXTENT_PX, spritePresentation, spriteIntersectsViewport } from '@/game/assets/sprite-presentation';
import { GameSpriteRegistry } from '@/game/assets/gameSpriteRegistry';
import { manifestUseKeys, resolveManifestTexture } from '@/game/assets/manifest-bindings.mjs';
import type { GameTextureManifestEntry } from '@/game/assets/resolveGameAssetUrl';
import type { SceneryChunk } from '@/modules/game-protocol/world-scenery';
import {
  buildingTypeToTextureKey,
  CHARACTER_DISPLAY_HEIGHT_PX,
  characterSpriteScale,
  heroTextureKey,
  type HeroFacing,
  MARKET_STALL_TEXTURE_KEY,
  objectToTextureKey,
  PLAZA_FOUNTAIN_TEXTURE_KEY,
  PROCEDURAL_TOWN_HOUSE_FACADE_KEYS,
  resolveBuildingSpriteVariant,
  restCampTextureKeys,
  resolveEnemySouthTextureKey,
  terrainToTextureKey,
  TOWN_HALL_TEXTURE_KEY,
} from '@/game/assets/spriteMapping';
import {
  worldToScreen,
  screenToWorld,
  EDITOR_OBJECT_ANCHOR_PX,
  type EditorOverlayItem,
  type ScreenPoint,
  type WorldPoint,
} from './coordinates';
import { WorldDepthContainer } from './depth-order';
import { FarPropCache, type FarProp, type PropDepthCut } from './far-prop-cache';
import { heroRevealBounds, heroRevealEllipse, campRevealEllipse, campRevealTargets, ownedOccludedTargets, ownedRevealTargets } from './hero-reveal-geometry';
import { ActorVisibility, rectanglesOverlap, spriteBodyRect, type BodyRect, type VisibilityEntry } from './actor-visibility';
import { PresentationCache } from './presentation-cache';
import { groundStride, overviewChunks, GroundChunkCache, type GroundChunk } from './ground-chunks';
import { RuntimeSceneIndex, type RuntimeScene } from '@/modules/game-procedural/runtime-scenes';
import { biomeObjectTextureKey, biomeFloorTextureKeys, coherentTerrain, biomeFloorTransitionTextureKey, biomeFloorType, biomeFloorMirror, biomeTileLocalEdge, presentationStyledRoadPaths, visibleRoadSegments, roadRibbonPolygon, type BiomeFloorType, type BiomeTileEdge, type ScreenBounds } from './terrain-presentation';
import { buildingGroundAnchorY } from './building-ground-contact';
import { authoredTextureKey } from '@/game/assets/authored-town-art';
import { authoredBuildingPresentation } from './authored-building-presentation';
import { makeIsoRepeatTexture } from './iso-repeat-texture';
import { authoredPavingMaterial, authoredPavingMatrix } from './authored-paving';
import { FortificationSprites } from './fortification-sprites';
import { fortificationsIntersectViewport } from './fortification-viewport';
import { AmbientInhabitants } from './ambient-inhabitants';
import { blendBiomeFloorPixels } from './biome-floor-blend';
import { proceduralTownBuildings } from './procedural-town-layout';
import { viewportHalfExtents } from '@/game/viewport-bounds';
import {
  proceduralTerrain,
  type WorldTerrainContext,
} from '@/modules/game-procedural/procedural';

type SpritePoolEntry = {
  sprite: Sprite;
  textureKey: string;
  worldX?: number;
  worldY?: number;
  hiddenSinceMs?: number;
  presentationRevision?: number;
  bodyRect?: BodyRect;
};

const SPRITE_HIDDEN_EVICT_MS = 3000;
const TERRAIN_SEAM_BLEED_SCALE = 1.002;
const FLAT_TERRAIN_SEAM_BLEED_SCALE = 1.04;
const GROUND_CAMERA_BOUNDS_QUANTIZE_PX = TILE_HEIGHT;
const GROUND_FALLBACK_COLORS: Readonly<Record<string, number>> = {
  grass: 0x596a43, road: 0x8a795f, dirt: 0x785d43, stone: 0x77766f, plaza: 0x898577,
  forest_floor: 0x4e6048, ruins_floor: 0x706957, canyon_floor: 0xa17c4c, swamp_floor: 0x4e625b,
  river_water: 0x476d78, river_shore: 0x7e866c, volcanic_floor: 0x604c49, astral_floor: 0x637282,
  winter_floor: 0x8a9598, jungle_floor: 0x4e6848,
};

function fallbackGroundColor(terrain: string): number {
  return GROUND_FALLBACK_COLORS[terrain] ?? GROUND_FALLBACK_COLORS.grass!;
}

export {
  worldToScreen,
  screenToWorld,
  EDITOR_OBJECT_ANCHOR_PX,
  type EditorOverlayItem,
  type ScreenPoint,
  type WorldPoint,
};

/**
 * Main renderer. Wraps a PixiJS Application and manages the scene graph.
 *
 * Terrain follows server towns + road ring, then active route waypoints; wild tiles use nearest town biome.
 *
 * Scene structure:
 *   app.stage
 *     -> worldContainer (moved by camera)
 *       -> groundLayer  (flat tiles, plazas, shadows)
 *       -> entityLayer  (props, buildings, characters, common ground-contact depth)
 *       -> effectLayer  (particles, flashes)
 *       -> labelLayer   (world-relative labels, icons, bubbles and editor UI)
 *     -> uiContainer    (fixed HUD elements rendered in Pixi, if any)
 */
export class GameRenderer {
  private _heroAnimationAssets = new HeroAnimationAssets();
  private _restIdleAssets = new RestIdleAssets();
  private _individualIdleAssets = new IndividualIdleAssets();
  private _meshyNpcAssets = new MeshyNpcAssets();
  private _npcPresentationClock = new NPCPresentationClock();
  private _npcServerNowMs = Date.now();
  private _npcServiceFacings = new Map<number, { dx: number; dy: number }>();
  private _serviceNpcAnimations = new Map<string, ServiceNpcAnimation>();
  private _restActivityClock = new RestActivityClock();
  private _restActivityView: RestActivityView | null = null;
  get restActivityView(): Readonly<RestActivityView> | null { return this._restActivityView ? { ...this._restActivityView } : null; }
  private _heroPresentation: GameTextureManifestEntry | undefined;
  app: Application;
  worldContainer: Container;
  groundLayer: Container;
  entityLayer: Container;
  effectLayer: Container;
  labelLayer: Container;
  uiContainer: Container;

  private _initialized = false;
  private _destroyed = false;
  private _initializing = false;
  private _appInitialized = false;
  private _resourcesDisposed = false;
  private _contextLost = false;
  private _initPromise: Promise<void> | null = null;

  /** Town ring + active route for procedural ground (null until towns loaded). */
  private _worldTerrainContext: WorldTerrainContext | null = null;

  // Sprite rendering
  private _spriteRegistry: GameSpriteRegistry;
  private _groundSpriteLayer: Container;
  private _roadRibbon = new Graphics();
  private _tileSpritePool = new Map<string, SpritePoolEntry>();
  private _objectSpritePool = new Map<string, SpritePoolEntry>();
  private _tileSpriteFreeList: Sprite[] = [];
  private _biomeBlendTextures = new Map<string, Texture>();
  private _biomeFloorFrames = new Map<Texture, Map<number, ImageData>>();
  private _floorMaterialOverride: BiomeFloorType | null = null;
  private _usedBiomeBlendTextures = new Set<Texture>();
  private _objectSpriteFreeList: Sprite[] = [];
  private _usedTileSprites = new Set<string>();
  private _usedObjectSprites = new Set<string>();
  private _townObjectSpritePool = new Map<string, SpritePoolEntry>();
  private _townObjectSpriteFreeList: Sprite[] = [];
  private _usedTownObjectSprites = new Set<string>();
  private _emptySpriteSet = new Set<string>();
  private _buildingSpritePool = new Map<string, SpritePoolEntry>();
  private _characterSpritePool = new Map<string, SpritePoolEntry>();
  private _npcSpritePool = new Map<string, SpritePoolEntry>();
  private _usedBuildingSprites = new Set<string>();
  private _usedNpcSprites = new Set<string>();
  private _actorVisibility = new ActorVisibility();
  private _fortifications: FortificationSprites | null = null;
  private _ambientInhabitants: AmbientInhabitants | null = null;
  private _terrainCache = new PresentationCache<string>();
  private _terrainDisplayCache = new PresentationCache<string>();
  private _maximumPropExtent: number | null = null;
  private _groundDirty = true;
  private _overviewLayer = new Container();
  private _overviewChunks = new GroundChunkCache<{ sprite: Sprite; texture: Texture; incomplete: boolean }>(entry => {
    entry.sprite.destroy({ texture: true });
    entry.texture.destroy(true);
  });
  private _runtimeScenes: RuntimeSceneIndex | null = null;
  private _sceneProps = new PresentationCache<readonly FarProp[]>(512);
  private _farProps: FarPropCache;
  private readonly _unlimitedTextureCache: boolean;
  private _farPropView: { camera: CameraView; width: number; height: number; stride: number } | null = null;
  private _lastObjectViewKey = '';
  private _presentationCache = new Map<string, { texture: Texture; revision: number | undefined;
    value: ReturnType<typeof spritePresentation> }>();
  private _lastGroundTextureRevision: number | undefined = -1;
  private _lastGroundStride = 1;
  private _groundChunkBuilds = 0;
  private _groundChunkSamples = 0;
  private _townRebuilds = 0;
  private _onContextLost = (event: Event): void => {
    event.preventDefault();
    this._contextLost = true;
  };
  private _onContextRestored = (): void => {
    if (this._destroyed) return;
    this._contextLost = false;
    // WebGL text uploads return their canvas to Pixi's pool. The old GPU text
    // cache cannot reupload those pixels; unload all users before rerasterizing.
    const texts = new Set<Text>([...this._townLabelPool, ...this._npcLabelPool, ...this._nearbyHeroLabelPool]);
    const collectText = (node: Container): void => {
      if (node instanceof Text) texts.add(node);
      for (const child of node.children) collectText(child);
    };
    collectText(this.app.stage);
    for (const text of texts) text.unload();
    // Pixi restores uploaded asset sources. Generated render targets must be rebuilt.
    this._clearBiomeBlendTextures();
    this._overviewChunks.clear(); this._sceneProps.clear();
    this._farProps.clear(); this._presentationCache.clear();
    this._authoredCobbleTexture?.destroy(true); this._authoredCobbleTexture = null;
    this._lastObjectViewKey = '';
    this._lastGroundBounds = null;
    this._groundDirty = true;
    this._townDrawDirty = true;
  };

  /** Presentation diagnostics only; callers cannot mutate renderer-owned caches. */
  getMapRenderStats(): Readonly<{ stride: number; groundChunks: number; groundCacheBytes: number;
    groundChunkBuilds: number; groundSamples: number; terrainSprites: number; townRebuilds: number; overviewIncompleteChunks: number;
    propTextures: number; propCacheBytes: number; propBuilds: number; individualFarProps: number }> {
    return { stride: this._lastGroundStride, groundChunks: this._overviewChunks.size,
      groundCacheBytes: this._overviewChunks.size * 260 * 260 * 4,
      groundChunkBuilds: this._groundChunkBuilds, groundSamples: this._groundChunkSamples,
      terrainSprites: this._tileSpritePool.size, townRebuilds: this._townRebuilds,
      overviewIncompleteChunks: [...this._overviewChunks.values()].filter(entry => entry.incomplete).length,
      propTextures: this._farProps?.stats.textures ?? 0, propCacheBytes: this._farProps?.stats.bytes ?? 0,
      propBuilds: this._farProps?.stats.builds ?? 0, individualFarProps: this._farProps?.stats.fallbackProps ?? 0 };
  }
  /** Manual local diagnostics. Never includes hero/session data or starts a render loop. */
  captureDiagnostics(): Record<string, unknown> & { errors: string[] } {
    const errors: string[] = [];
    const report: Record<string, unknown> & { errors: string[] } = {
      pixelByteAccounting: 'RGBA pixel estimates; not total process or GPU memory',
      schema: 1, modulePath: new URL(import.meta.url).pathname, capturedAt: new Date().toISOString(),
      initialized: this._initialized, destroyed: this._destroyed, contextLost: this._contextLost,
      manifestVersion: this._spriteRegistry.manifest?.version ?? null,
      userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
      zoom: this._initialized && !this._destroyed && !this.worldContainer.destroyed ? this.worldContainer.scale.x : null, groundBounds: this._lastGroundBounds ? { ...this._lastGroundBounds } : null,
      errors,
    };
    const capture = (name: string, read: () => unknown) => {
      try { report[name] = read(); }
      catch (error) { errors.push(`${name}: ${error instanceof Error ? error.message : String(error)}`); }
    };
    capture('memory', () => this.getTextureMemoryStats());
    capture('map', () => this.getMapRenderStats());
    capture('pools', () => Object.fromEntries([
      ['terrain', this._tileSpritePool], ['objects', this._objectSpritePool],
      ['townObjects', this._townObjectSpritePool], ['buildings', this._buildingSpritePool],
      ['hero', this._characterSpritePool], ['npcs', this._npcSpritePool], ['nearby', this._nearbyHeroSpritePool],
    ].map(([name, pool]) => [name, inspectTextureReferences([...(pool as Map<string, SpritePoolEntry>).values()].map(e => e.sprite))])));
    capture('generated', () => {
      let blendBytes = 0, destroyedBlends = 0;
      const canvasCenterAlpha: Array<number | null> = [];
      for (const texture of this._biomeBlendTextures.values()) {
        if (texture.destroyed || !texture.source || texture.source.destroyed) { destroyedBlends++; continue; }
        const source = texture.source;
        blendBytes += source.pixelWidth * source.pixelHeight * 4;
        const canvas = source.resource;
        if (canvasCenterAlpha.length < 16 && typeof HTMLCanvasElement !== 'undefined' && canvas instanceof HTMLCanvasElement) {
          const context = canvas.getContext('2d');
          if (!context) { errors.push('generated: Canvas 2D context unavailable'); canvasCenterAlpha.push(null); }
          else {
            try { canvasCenterAlpha.push(context.getImageData(Math.floor(canvas.width / 2), Math.floor(canvas.height / 2), 1, 1).data[3]!); }
            catch (error) { canvasCenterAlpha.push(null); errors.push(`generated: ${error instanceof Error ? error.message : String(error)}`); }
          }
        }
      }
      let cpuFrameBytes = 0, cpuFrames = 0;
      for (const frames of this._biomeFloorFrames.values()) { cpuFrames += frames.size; for (const frame of frames.values()) cpuFrameBytes += frame.data.byteLength; }
      let overviewTextures = 0, overviewBytes = 0, destroyedOverviewTextures = 0;
      for (const entry of this._overviewChunks.values()) {
        overviewTextures++;
        const texture = entry.texture;
        if (texture.destroyed || !texture.source || texture.source.destroyed) { destroyedOverviewTextures++; continue; }
        overviewBytes += texture.source.pixelWidth * texture.source.pixelHeight * 4;
      }
      return { blends: this._biomeBlendTextures.size, blendBytes, destroyedBlends,
        overviewTextures, overviewBytes, destroyedOverviewTextures,
        cpuFrameViews: this._biomeFloorFrames.size, cpuFrames, cpuFrameBytes, canvasCenterAlpha };
    });
    if (!this._initialized || this._destroyed) { errors.push('Renderer is not initialized'); return report; }
    if (this._contextLost) { errors.push('WebGL context is lost'); return report; }
    capture('gpu', () => {
      const gl = (this.app.renderer as unknown as { gl?: WebGLRenderingContext | WebGL2RenderingContext }).gl;
      if (!gl) throw new Error('WebGL is unavailable');
      if (gl.isContextLost()) throw new Error('WebGL context is lost');
      const debug = gl.getExtension('WEBGL_debug_renderer_info');
      capture('pixels', () => captureFramePixels(gl, () => this.app.render()));
      report.glError = gl.getError();
      return { version: gl.getParameter(gl.VERSION), vendor: gl.getParameter(gl.VENDOR),
        renderer: gl.getParameter(gl.RENDERER),
        unmaskedVendor: debug ? gl.getParameter(debug.UNMASKED_VENDOR_WEBGL) : null,
        unmaskedRenderer: debug ? gl.getParameter(debug.UNMASKED_RENDERER_WEBGL) : null,
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE), maxTextureUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
        maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) };
    });
    return report;
  }
  getTextureMemoryStats() {
    return { contextLost: this._contextLost, sprites: this._spriteRegistry.stats,
      animations: this._heroAnimationAssets.stats() };
  }
  private _lastGroundBounds:
    | {
        startX: number;
        endX: number;
        startY: number;
        endY: number;
      }
    | null = null;
  /** Incremented on each full ground rebuild; used to throttle eviction passes. */
  private _groundRebuildCounter = 0;

  // Reusable Graphics (avoid GC). UI/combat overlays; world tiles are sprite-only.
  private _heroGfx: Graphics | null = null;
  /** Second adventurer silhouette during hero_meet (opponent at server stand position). */
  private _meetPartnerGfx: Graphics | null = null;
  private _meetPartnerLabel: Text | null = null;
  /** Tent + campfire while resting in the wild phase (roadside / adventure inline). */
  private _restCampGfx: Graphics | null = null;
  private _enemyGfx: Graphics | null = null;
  private _meetBubbleSlots: Array<{
    gfx: Graphics;
    txt: Text;
  }> = [];
  private _heroNameText: Text | null = null;
  private _heroName = '';

  // Town rendering
  private _townGfx: Graphics | null = null;
  private _authoredPlazaGfx: Graphics | null = null;
  private _editorOverlayGfx: Graphics | null = null;
  private _townIconGfx: Graphics | null = null;
  /** Purpose icons above server building sprites (world-relative UI overlay). */
  private _buildingPurposeGfx: Graphics | null = null;
  private _signpostFallbacks = new Map<number, { gfx: Graphics; lastSeen: number }>();
  private _townLabels: Text[] = [];
  private _townLabelPool: Text[] = [];

  // NPC rendering
  private _npcGfx: Graphics | null = null;
  private _npcLabels: Text[] = [];
  private _npcLabelPool: Text[] = [];

  // Nearby hero rendering
  private _nearbyHeroGfx: Graphics | null = null;
  private _nearbyHeroLabels: Text[] = [];
  private _nearbyHeroLabelPool: Text[] = [];
  private _nearbyHeroSpritePool = new Map<string, SpritePoolEntry>();
  private _usedNearbyHeroSprites = new Set<string>();

  private _townDrawDirty = true;
  private _lastTownViewport = '';
  private _lastTownCameraX = Number.NaN;
  private _lastTownCameraY = Number.NaN;
  private _lastTownsRef: readonly ReadonlyData<TownData>[] | null = null;
  private _authoredCobbleTexture: Texture | null = null;
  private _lastTownTextureRevision = -1;
  private _lastTownCount = -1;

  private _cachedPresentation(key: string, texture: Texture): ReturnType<typeof spritePresentation> {
    const revision = this._spriteRegistry.revision;
    const cached = this._presentationCache.get(key);
    if (cached && cached.texture === texture && cached.revision === revision) return cached.value;
    const value = spritePresentation(resolveManifestTexture(this._spriteRegistry.manifest, key), texture.width, texture.height);
    this._presentationCache.set(key, { texture, revision, value });
    return value;
  }

  private _applyPresentation(entry: SpritePoolEntry, legacyScale: number): void {
    const texture = entry.sprite.texture;
    const p = this._cachedPresentation(entry.textureKey, texture);
    if (p) entry.sprite.anchor.set(p.anchorX, p.anchorY);
    entry.sprite.scale.set(p?.scale ?? legacyScale);
  }

  private _ensureSprite(
    pool: Map<string, SpritePoolEntry>,
    poolKey: string,
    textureKey: string,
    texture: SpritePoolEntry['sprite']['texture'],
    layer: Container,
    worldX?: number,
    worldY?: number,
    freeList?: Sprite[],
  ): SpritePoolEntry {
    let entry = pool.get(poolKey);
    if (!entry) {
      const sprite = freeList && freeList.length > 0
        ? (freeList.pop() as Sprite)
        : new Sprite(texture);
      sprite.texture = texture;
      sprite.anchor.set(0.5, 1);
      sprite.roundPixels = true;
      sprite.label = poolKey;
      if (sprite.parent !== layer) layer.addChild(sprite);
      entry = { sprite, textureKey, worldX, worldY };
      pool.set(poolKey, entry);
      return entry;
    }
    if (entry.textureKey !== textureKey || entry.sprite.texture !== texture) {
      entry.sprite.texture = texture;
      entry.sprite.anchor.set(0.5, 1);
      entry.textureKey = textureKey;
    }
    if (typeof worldX === 'number' && typeof worldY === 'number') {
      entry.worldX = worldX;
      entry.worldY = worldY;
    }
    if (entry.sprite.parent !== layer) layer.addChild(entry.sprite);
    return entry;
  }

  /**
   * Hide pooled sprites not in `used`. After {@link SPRITE_HIDDEN_EVICT_MS} of being hidden,
   * remove from the pool and recycle or destroy the sprite.
   */
  private _hideUnusedSprites(
    pool: Map<string, SpritePoolEntry>,
    used: Set<string>,
    nowMs: number,
    freeList?: Sprite[],
    maxFreeListSize?: number,
  ): void {
    const toRemove: string[] = [];
    for (const [key, entry] of pool) {
      if (used.has(key)) {
        entry.sprite.visible = true;
        entry.hiddenSinceMs = undefined;
        continue;
      }
      entry.sprite.visible = false;
      if (entry.hiddenSinceMs === undefined) {
        entry.hiddenSinceMs = nowMs;
      } else if (nowMs - entry.hiddenSinceMs >= SPRITE_HIDDEN_EVICT_MS) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      const entry = pool.get(key);
      if (!entry) continue;
      pool.delete(key);
      const sprite = entry.sprite;
      sprite.visible = false;
      sprite.texture = Texture.EMPTY;
      if (
        freeList !== undefined &&
        maxFreeListSize !== undefined &&
        freeList.length < maxFreeListSize
      ) {
        freeList.push(sprite);
      } else {
        sprite.destroy();
      }
    }
  }

  /** Drop GPU cache before mutating vector paths (Pixi 8: prefer cacheAsTexture over legacy cacheAsBitmap). */
  private _releaseGraphicsTextureCache(gfx: Graphics | null): void {
    if (!gfx?.isCachedAsTexture) return;
    gfx.cacheAsTexture(false);
  }

  private _evictSpritesOutsideTileBounds(
    pool: Map<string, SpritePoolEntry>,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    freeList: Sprite[],
    maxFreeListSize: number,
  ): void {
    for (const [key, entry] of pool) {
      const wx = entry.worldX ?? Number.NaN;
      const wy = entry.worldY ?? Number.NaN;
      if (!Number.isFinite(wx) || !Number.isFinite(wy)) continue;
      if (wx >= minX && wx <= maxX && wy >= minY && wy <= maxY) continue;
      pool.delete(key);
      const sprite = entry.sprite;
      sprite.visible = false;
      sprite.texture = Texture.EMPTY;
      if (freeList.length < maxFreeListSize) {
        freeList.push(sprite);
      } else {
        sprite.destroy();
      }
    }
  }

  /** Called from GameEngine when towns or route waypoints change. */
  setWorldTerrainContext(ctx: WorldTerrainContext | null): void {
    this._worldTerrainContext = ctx;
    this._runtimeScenes?.clear();
    this._runtimeScenes = ctx && ctx.authoritativeScenery === undefined && ctx.runtimeScenes !== false ? new RuntimeSceneIndex(ctx) : null;
    this._sceneProps.clear();
    this._overviewChunks.clear();
    this._farProps.clear();
    this._lastObjectViewKey = '';
    this._terrainCache.clear();
    this._terrainDisplayCache.clear();
    this._groundDirty = true;
  }

  /** Server props do not change ground geometry or the pixels of completed ground chunks. */
  setAuthoritativeScenery(chunks: readonly SceneryChunk[]): void {
    if (this._destroyed || !this._worldTerrainContext) return;
    this._worldTerrainContext = { ...this._worldTerrainContext, authoritativeScenery: chunks };
    this._runtimeScenes?.clear();
    this._runtimeScenes = null;
    this._sceneProps.clear();
    this._farProps.clear();
    this._lastObjectViewKey = '';
    this._groundDirty = true;
  }

  /** Preview material selection uses the same ground path, camera and source orientations as the game. */
  setFloorMaterialOverride(type: BiomeFloorType | null): void {
    if (type === this._floorMaterialOverride) return;
    this._floorMaterialOverride = type;
    this._overviewChunks.clear();
    this._groundDirty = true;
  }

  private _floorType(wx: number, wy: number, baseKey: string): BiomeFloorType {
    return this._floorMaterialOverride ?? biomeFloorType(wx, wy, baseKey);
  }

  private _floorTextureKey(baseKey: string, wx: number, wy: number): string | null {
    const keys = biomeFloorTextureKeys(baseKey);
    return keys?.[this._floorType(wx, wy, baseKey)] ?? null;
  }

  private _biomeFloorNeighbors(wx: number, wy: number, stride: number, baseKey: string,
    center: BiomeFloorType, overview: boolean, texturedRoads: boolean): readonly BiomeFloorType[] {
    const offsets = [
      [0, -1], [1, 0], [0, 1], [-1, 0],
      [1, -1], [1, 1], [-1, 1], [-1, -1],
    ] as const;
    const profile = this._spriteRegistry.manifest?.worldPresentation;
    return offsets.map(([dx, dy]) => {
      const nx = wx + dx * stride, ny = wy + dy * stride;
      const original = this._terrainCache.getOrCreate(`${nx},${ny}`,
        () => proceduralTerrain(nx, ny, this._worldTerrainContext));
      const display = overview
        ? profile?.coherentTerrain === true || original === 'road'
          ? coherentTerrain(nx, ny, this._worldTerrainContext, original, true) : original
        : profile?.coherentTerrain === true
          ? coherentTerrain(nx, ny, this._worldTerrainContext, original, texturedRoads) : original;
      return terrainToTextureKey(display) === baseKey ? this._floorType(nx, ny, baseKey) : center;
    });
  }

  /** Read each source orientation once; composites share these immutable CPU pixels. */
  private _biomeFloorFrame(texture: Texture, mirror: { x: boolean; y: boolean }): ImageData | null {
    const orientation = Number(mirror.x) | (Number(mirror.y) << 1);
    let frames = this._biomeFloorFrames.get(texture);
    const cached = frames?.get(orientation);
    if (cached) return cached;
    const image = texture.source.resource as CanvasImageSource | undefined;
    if (!image) return null;
    const canvas = document.createElement('canvas');
    canvas.width = 96; canvas.height = 48;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.imageSmoothingEnabled = false;
    context.setTransform(mirror.x ? -1 : 1, 0, 0, mirror.y ? -1 : 1,
      mirror.x ? 96 : 0, mirror.y ? 48 : 0);
    const frame = texture.frame;
    context.drawImage(image, frame.x, frame.y, frame.width, frame.height, 0, 0, 96, 48);
    const pixels = context.getImageData(0, 0, 96, 48);
    if (!frames) { frames = new Map(); this._biomeFloorFrames.set(texture, frames); }
    frames.set(orientation, pixels);
    return pixels;
  }

  /** Build only the neighborhood mixes actually encountered; reuse the bitmap by material layout. */
  private _biomeFloorBlendTexture(baseKey: string, center: BiomeFloorType,
    neighbors: readonly BiomeFloorType[], wx: number, wy: number, stride: number): Texture | null {
    const tileKeys = biomeFloorTextureKeys(baseKey);
    if (!tileKeys || neighbors.length !== 8 || neighbors.every(type => type === center)) return null;
    const config = [center, ...neighbors];
    const offsets = [[0, 0], [0, -1], [1, 0], [0, 1], [-1, 0],
      [1, -1], [1, 1], [-1, 1], [-1, -1]] as const;
    // Orient each material before blending. The output remains in world directions.
    const mirrors = offsets.map(([dx, dy]) => biomeFloorMirror(wx + dx * stride, wy + dy * stride));
    const cacheKey = `${baseKey}:${config.join('')}:${mirrors.map(m => Number(m.x) | (Number(m.y) << 1)).join('')}`;
    const cached = this._biomeBlendTextures.get(cacheKey);
    if (cached) {
      this._biomeBlendTextures.delete(cacheKey);
      this._biomeBlendTextures.set(cacheKey, cached);
      this._usedBiomeBlendTextures.add(cached);
      return cached;
    }

    const size = { width: 96, height: 48 };
    const frames = config.map((type, index) => {
      const tileKey = tileKeys[type];
      if (!tileKey) return null;
      const texture = this._spriteRegistry.getTexture(tileKey);
      return texture ? this._biomeFloorFrame(texture, mirrors[index]!) : null;
    });
    if (frames.some(frame => frame === null)) return null;

    const canvas = document.createElement('canvas');
    canvas.width = size.width; canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    const output = context.createImageData(size.width, size.height);
    output.data.set(blendBiomeFloorPixels(frames as ImageData[]));
    context.putImageData(output, 0, 0);
    const texture = Texture.from(canvas);
    texture.source.scaleMode = 'nearest';
    this._biomeBlendTextures.set(cacheKey, texture);
    this._usedBiomeBlendTextures.add(texture);
    return texture;
  }

  private _trimBiomeBlendTextures(limit = 96): void {
    if (this._unlimitedTextureCache) return;
    if (this._biomeBlendTextures.size <= limit) return;
    for (const [key, texture] of this._biomeBlendTextures) {
      if (this._biomeBlendTextures.size <= limit) break;
      if (this._usedBiomeBlendTextures.has(texture)) continue;
      this._biomeBlendTextures.delete(key);
      texture.destroy(true);
    }
  }

  private _clearBiomeBlendTextures(): void {
    for (const texture of this._biomeBlendTextures.values()) texture.destroy(true);
    this._biomeBlendTextures.clear();
    this._usedBiomeBlendTextures.clear();
    this._biomeFloorFrames.clear();
  }

  /** Optional service markers are useful in tools, but obscure the authored town art. */
  setPurposeMarkersVisible(visible: boolean): void {
    if (this._buildingPurposeGfx) this._buildingPurposeGfx.visible = visible;
  }

  /** Force town vector/sprites to redraw on the next frame. */
  invalidateTowns(): void {
    this._townDrawDirty = true;
    this._lastTownsRef = null;
  }

  constructor(options: { spriteCacheMaxBytes?: number | null; unlimitedTextureCache?: boolean } = {}) {
    this._unlimitedTextureCache = options.unlimitedTextureCache === true;
    if (this._unlimitedTextureCache) {
      this._heroAnimationAssets = new HeroAnimationAssets({ maxBytes: Infinity });
      this._restIdleAssets = new RestIdleAssets({ maxBytes: Infinity });
      this._individualIdleAssets = new IndividualIdleAssets({ maxBytes: Infinity });
      this._meshyNpcAssets = new MeshyNpcAssets({ maxBytes: Infinity });
      this._overviewChunks = new GroundChunkCache(entry => {
        entry.sprite.destroy({ texture: true }); entry.texture.destroy(true);
      }, Infinity);
      this._terrainCache = new PresentationCache(Infinity);
      this._terrainDisplayCache = new PresentationCache(Infinity);
      this._sceneProps = new PresentationCache(Infinity);
    }
    this._spriteRegistry = new GameSpriteRegistry({
      maxResidentBytes: this._unlimitedTextureCache ? null : options.spriteCacheMaxBytes === undefined ? 96 * 1024 * 1024 : options.spriteCacheMaxBytes,
    });
    this.app = new Application();
    // @ts-expect-error Pixi devtools hook
    globalThis.__PIXI_APP__ = this.app;
    this.worldContainer = new Container();
    this.groundLayer = new Container();
    this.entityLayer = new WorldDepthContainer();
    this.effectLayer = new Container();
    this.labelLayer = new Container();
    this.labelLayer.sortableChildren = true;
    this.uiContainer = new Container();
    this._groundSpriteLayer = new Container();
    this._farProps = new FarPropCache(this.entityLayer, options => this.app.renderer.generateTexture(options),
      bounds => this._queryPropCandidates(bounds),
      this._unlimitedTextureCache ? Infinity : undefined, this._unlimitedTextureCache ? Infinity : undefined);
  }

  get initialized(): boolean {
    return this._initialized;
  }

  /** Initialize the PixiJS application and attach to canvas container */
  init(canvasContainer: HTMLElement, manifestUrl?: string): Promise<void> {
    if (this._destroyed || this._initialized) return Promise.resolve();
    if (this._initPromise) return this._initPromise;
    this._initializing = true;
    this._initPromise = this._initialize(canvasContainer, manifestUrl).catch((error) => {
      this._destroyed = true;
      throw error;
    }).finally(() => {
      this._initializing = false;
      if (this._destroyed) this._disposeResources();
    });
    return this._initPromise;
  }

  private async _initialize(canvasContainer: HTMLElement, manifestUrl?: string): Promise<void> {
    const viewport = getViewport();

    await this.app.init({
      width: viewport.width,
      height: viewport.height,
      backgroundColor: 0x1a1a2e,
      resolution: Math.min(window.devicePixelRatio, 2),
      autoDensity: true,
      autoStart: false,
      sharedTicker: false,
      antialias: false,
      powerPreference: 'high-performance',
    });

    this._appInitialized = true;
    if (this._destroyed) return;
    canvasContainer.appendChild(this.app.canvas as HTMLCanvasElement);
    this.app.canvas.addEventListener('webglcontextlost', this._onContextLost);
    this.app.canvas.addEventListener('webglcontextrestored', this._onContextRestored);

    // Missing or incompatible pinned generations must reach session recovery.
    await this._spriteRegistry.loadAll(manifestUrl);

    if (this._destroyed) return;

    // Build scene graph
    this.worldContainer.addChild(this.groundLayer);
    this.worldContainer.addChild(this.entityLayer);
    this.entityLayer.sortableChildren = true;
    this._fortifications = new FortificationSprites(this.entityLayer, key => this._spriteRegistry.getTexture(key));
    this._ambientInhabitants = new AmbientInhabitants(this.entityLayer, this._meshyNpcAssets);
    this.worldContainer.addChild(this.effectLayer);
    this.worldContainer.addChild(this.labelLayer);
    this.app.stage.addChild(this.worldContainer);
    this.app.stage.addChild(this.uiContainer);

    // Center the world container and apply zoom
    this.worldContainer.x = viewport.width / 2;
    this.worldContainer.y = viewport.height / 2;
    this.worldContainer.scale.set(MAP_ZOOM);

    // Create reusable graphics objects
    this.groundLayer.sortableChildren = true;
    this.groundLayer.addChild(this._groundSpriteLayer);
    this._roadRibbon.label = 'presentation-road';
    // Road approaches must remain visible over settlement ground/plaza fills.
    this._roadRibbon.zIndex = 4.5;
    this.groundLayer.addChild(this._roadRibbon);
    this._overviewLayer.zIndex = 0;
    this.groundLayer.addChild(this._overviewLayer);
    this._groundSpriteLayer.zIndex = 0;
    this._groundSpriteLayer.sortableChildren = true;

    this._heroGfx = new Graphics();
    this.entityLayer.addChild(this._heroGfx);

    this._meetPartnerGfx = new Graphics();
    this.entityLayer.addChild(this._meetPartnerGfx);

    this._restCampGfx = new Graphics();
    this.entityLayer.addChild(this._restCampGfx);

    this._enemyGfx = new Graphics();
    this.entityLayer.addChild(this._enemyGfx);

    for (let i = 0; i < 3; i++) {
      const gfx = new Graphics();
      this.labelLayer.addChild(gfx);
      const txt = new Text({
        text: '',
        style: new TextStyle({
          fontSize: 10,
          fontFamily: 'system-ui, sans-serif',
          fill: 0x333333,
          wordWrap: true,
          wordWrapWidth: 200,
          align: 'center',
        }),
      });
      txt.anchor.set(0.5, 0.5);
      txt.visible = false;
      this.labelLayer.addChild(txt);
      this._meetBubbleSlots.push({ gfx, txt });
    }

    this._heroNameText = new Text({
      text: '',
      style: new TextStyle({
        fontSize: 11,
        fontFamily: 'system-ui, sans-serif',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 3 },
        align: 'center',
      }),
    });
    this._heroNameText.anchor.set(0.5, 0.5);
    this._heroNameText.visible = false;
    this.labelLayer.addChild(this._heroNameText);

    this._meetPartnerLabel = new Text({
      text: '',
      style: new TextStyle({
        fontSize: 11,
        fontFamily: 'system-ui, sans-serif',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 3 },
        align: 'center',
      }),
    });
    this._meetPartnerLabel.anchor.set(0.5, 0.5);
    this._meetPartnerLabel.visible = false;
    this.labelLayer.addChild(this._meetPartnerLabel);

    // Town graphics (drawn between ground and entity layers)
    this._townGfx = new Graphics();
    this._townGfx.zIndex = 4;
    this._authoredPlazaGfx = new Graphics();
    this._authoredPlazaGfx.label = 'authored-plaza-ground';
    this._authoredPlazaGfx.zIndex = 4.75;
    this.groundLayer.addChild(this._authoredPlazaGfx);
    this.groundLayer.addChild(this._townGfx);

    this._townIconGfx = new Graphics();
    this._townIconGfx.zIndex = 5;
    this.groundLayer.addChild(this._townIconGfx);

    this._buildingPurposeGfx = new Graphics();
    this._buildingPurposeGfx.zIndex = 5;
    this.labelLayer.addChild(this._buildingPurposeGfx);

    // Flat NPC and nearby hero shadows
    this._npcGfx = new Graphics();
    this.groundLayer.addChild(this._npcGfx);
    this._npcGfx.zIndex = 6;

    // Nearby hero graphics
    this._nearbyHeroGfx = new Graphics();
    this.groundLayer.addChild(this._nearbyHeroGfx);
    this._nearbyHeroGfx.zIndex = 6;

    this._editorOverlayGfx = new Graphics();
    this._editorOverlayGfx.zIndex = 40;
    this.labelLayer.addChild(this._editorOverlayGfx);

    this._initialized = true;
    console.info(`[Renderer] Initialized ${viewport.width}x${viewport.height} @${this.app.renderer.resolution}x`);
  }

  onAssetError: ((error: Error) => void) | null = null;
  private _assetError: Error | undefined;
  get assetError(): Error | undefined { return this._assetError; }
  private _frameViewport: ScreenBounds | null = null;
  private _firstCorrectFrame = false;
  get firstCorrectFrame(): boolean { return this._firstCorrectFrame; }

  /** Sole frame entry point. Consume presentation data, then submit one GPU frame. */
  renderFrame(frame: RenderSnapshot): void {
    if (!this._initialized || this._destroyed || this._contextLost || this._assetError) return;
    this._beginTextureFrame();
    if (!this._firstCorrectFrame) this._prepareFirstFrameDemand();
    const { camera, viewport, nowMs, hero, enemy } = frame;
    this._npcServerNowMs = this._npcPresentationClock.sample(nowMs, frame.serverTimeMs);
    this._restActivityView = this._restActivityClock.sample(nowMs, frame.restCamp && hero
      ? 'visible-wilderness-rest' : null);
    const zoom = camera.zoom ?? MAP_ZOOM;
    this._frameViewport = { minX: camera.finalX - viewport.width / (2 * zoom), maxX: camera.finalX + viewport.width / (2 * zoom),
      minY: camera.finalY - viewport.height / (2 * zoom), maxY: camera.finalY + viewport.height / (2 * zoom) };
    if (this.worldContainer.scale.x !== zoom) this._townDrawDirty = true;
    this.worldContainer.scale.set(zoom);
    this.worldContainer.position.set(
      Math.round(viewport.width / 2 - camera.finalX * zoom),
      Math.round(viewport.height / 2 - camera.finalY * zoom),
    );
    this.drawGround(camera, viewport.width, viewport.height);
    this.drawTowns(frame.towns, camera, viewport.width, viewport.height);
    this.drawTownObjects(frame.towns.flatMap((town) => town.objects ?? []), camera, viewport.width, viewport.height);
    if (frame.restCamp) this.drawRestCamp(frame.restCamp.x, frame.restCamp.y, nowMs);
    else this.clearRestCamp();
    if (hero) {
      this.drawHero(hero.x, hero.y, hero.animation, nowMs, hero.modelVariant, 'south', hero.motion, hero.id, frame.restCamp !== null);
      if (frame.restCamp) this._drawRestingHero(hero.x, hero.y, hero.modelVariant, nowMs);
    }
    else this.clearHero();
    if (frame.npcs.length) this.drawNPCs(frame.npcs, camera, viewport.width, viewport.height, nowMs);
    else this.clearNPCs();
    if (frame.nearbyHeroes.length) this.drawNearbyHeroes(frame.nearbyHeroes, camera, viewport.width, viewport.height, nowMs);
    else this.clearNearbyHeroes();
    this.drawHeroMeetBubbles(frame.bubbles, nowMs);
    if (enemy) this.drawEnemy(enemy.x, enemy.y, enemy.slug, nowMs);
    else this.clearEnemyCombat();
    this.sortEntities();
    // Finalization starts a fresh collection for editor callers, so snapshot
    // readiness first. Pending frames must still finish ownership/queue accounting.
    const caches = [this._spriteRegistry, this._heroAnimationAssets, this._restIdleAssets,
      this._individualIdleAssets, this._meshyNpcAssets];
    const frameReady = caches.every(cache => cache.frameReady);
    const error = caches.map(cache => cache.frameError).find(error => !!error)
      // A refused startup source cannot become ready by waiting in cooldown.
      // Keep the established streaming admission/retry policy after publication.
      ?? (!this._firstCorrectFrame ? caches.map(cache => cache.frameAdmissionError).find(error => !!error) : undefined);
    this._endTextureFrame();
    if (error) { this._assetError = error; this.onAssetError?.(error); return; }
    if (!this._firstCorrectFrame) {
      if (!frameReady) return;
      this._firstCorrectFrame = true;
    }
    this.app.render();
  }

  private _prepareFirstFrameDemand(): void {
    // Cached empty candidates are valid between source revisions during normal
    // streaming, but cannot stand in for a complete startup viewport.
    this._groundDirty = true; this._townDrawDirty = true;
    this._lastObjectViewKey = '';
    this._sceneProps.clear(); this._terrainDisplayCache.clear();
    this._overviewChunks.invalidate(entry => entry.incomplete);
  }

  private _beginTextureFrame(): void {
    this._spriteRegistry.beginFrame();
    this._heroAnimationAssets.beginFrame(); this._restIdleAssets.beginFrame(); this._individualIdleAssets.beginFrame(); this._meshyNpcAssets.beginFrame();
  }

  private _endTextureFrame(): void {
    const retained = new Set<Texture['source']>();
    const retain = (texture: Texture): void => {
      if (retained.has(texture.source)) return;
      retained.add(texture.source);
      this._spriteRegistry.retainTexture(texture);
      this._heroAnimationAssets.retainTexture(texture); this._restIdleAssets.retainTexture(texture);
      this._individualIdleAssets.retainTexture(texture); this._meshyNpcAssets.retainTexture(texture);
    };
    // Pooled sprites always rebind in _ensureSprite when reused. Cache sprites and
    // Graphics instructions can reappear without a redraw, so preserve those references.
    for (const pool of [this._tileSpritePool, this._objectSpritePool, this._townObjectSpritePool,
      this._buildingSpritePool, this._characterSpritePool, this._npcSpritePool, this._nearbyHeroSpritePool]) {
      for (const entry of pool.values()) if (!entry.sprite.visible) entry.sprite.texture = Texture.EMPTY;
    }
    const visit = (node: Container): void => {
      if (node instanceof Sprite) retain(node.texture);
      if (node instanceof Graphics) {
        for (const instruction of node.context.instructions) {
          if (instruction.action === 'texture') retain(instruction.data.image);
          else if (instruction.action === 'fill' || instruction.action === 'stroke') {
            const texture = instruction.data.style.texture;
            if (texture) retain(texture);
          }
        }
      }
      for (const child of node.children) visit(child);
    };
    visit(this.app.stage);
    this._spriteRegistry.endFrame();
    this._heroAnimationAssets.endFrame(); this._restIdleAssets.endFrame(); this._individualIdleAssets.endFrame(); this._meshyNpcAssets.endFrame();
    // Editor callers assemble their frame before renderEditorFrame, using the same ownership boundary.
    this._beginTextureFrame();
  }

  /** Render or clear the editor's isolated server-owned test actor. */
  drawEditorHero(hero: { x: number; y: number; moving?: boolean } | null, nowMs: number, motion?: HeroAnimationView): void {
    if (!hero) { this.clearHero(); return; }
    this.drawHero(hero.x, hero.y, hero.moving ? 'walk' : 'idle', nowMs, 0, 'south', motion);
  }

  /** Submit a frame assembled by the Town Editor's own animation loop. */
  renderEditorFrame(): void {
    if (!this._initialized || this._destroyed || this._contextLost) return;
    this.app.render();
    this._endTextureFrame();
  }

  private clearHero(): void {
    this._heroGfx?.clear();
    if (this._heroNameText) this._heroNameText.visible = false;
    this._hideUnusedSprites(this._characterSpritePool, this._emptySpriteSet, performance.now());
  }

  /** Handle window/viewport resize */
  resize(): void {
    if (!this._initialized) return;
    const viewport = getViewport();
    this.app.renderer.resize(viewport.width, viewport.height);
    this.worldContainer.x = viewport.width / 2;
    this.worldContainer.y = viewport.height / 2;
    this._townDrawDirty = true;
  }

  /**
   * Draw real ground textures and decorative sprites; optional textured road ribbons stay in the ground layer.
   * Terrain is procedurally generated — the world is endless.
   */
  drawGround(camera: CameraView, screenWidth: number, screenHeight: number): void {
    if (this._contextLost) return;
    const stride = groundStride(camera.zoom ?? MAP_ZOOM);
    const wasFar = this._farPropView !== null;
    this._farPropView = (camera.zoom ?? MAP_ZOOM) < .2 ? { camera, width: screenWidth, height: screenHeight, stride } : null;
    if (wasFar !== (this._farPropView !== null)) { this._groundDirty = true; this._lastObjectViewKey = ''; }
    if (!this._farPropView) this._farProps.hide();
    const revision = this._spriteRegistry.revision;
    if (revision !== this._lastGroundTextureRevision) {
      this._lastGroundTextureRevision = revision;
      // Completed blends own immutable pixels for this renderer's manifest.
      // Unrelated source arrivals/evictions must not destroy and rerasterize them.
      // Keep only CPU frame samples whose source view is still alive.
      for (const texture of this._biomeFloorFrames.keys()) {
        if (texture.destroyed || texture.source.destroyed) this._biomeFloorFrames.delete(texture);
      }
      // Baked chunks own their pixels, so unrelated source arrivals must not
      // force expensive rerasterization. Retry chunks that used fallback art.
      this._overviewChunks.invalidate(entry => entry.incomplete);
      this._sceneProps.clear(); this._farProps.clear();
      this._lastObjectViewKey = ''; this._presentationCache.clear();
      this._terrainDisplayCache.clear();
      this._maximumPropExtent = null;
      this._groundDirty = true;
    }
    if (stride !== this._lastGroundStride) { this._groundDirty = true; this._lastObjectViewKey = ''; }
    this._lastGroundStride = stride;
    this._groundSpriteLayer.visible = stride === 1;
    this._overviewLayer.visible = stride !== 1;
    const cx = camera.finalX;
    const cy = camera.finalY;
    const q = GROUND_CAMERA_BOUNDS_QUANTIZE_PX;
    const qcx = Math.round(cx / q) * q;
    const qcy = Math.round(cy / q) * q;
    const halfW = screenWidth / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_WIDTH * 2;
    const halfH = screenHeight / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_HEIGHT * 2;

    const worldCorners = [
      screenToWorld(qcx - halfW, qcy - halfH),
      screenToWorld(qcx + halfW, qcy - halfH),
      screenToWorld(qcx - halfW, qcy + halfH),
      screenToWorld(qcx + halfW, qcy + halfH),
    ];

    const renderPaddingTiles = 5;
    let minWX = Number.POSITIVE_INFINITY;
    let maxWX = Number.NEGATIVE_INFINITY;
    let minWY = Number.POSITIVE_INFINITY;
    let maxWY = Number.NEGATIVE_INFINITY;

    for (const corner of worldCorners) {
      if (corner.x < minWX) minWX = corner.x;
      if (corner.x > maxWX) maxWX = corner.x;
      if (corner.y < minWY) minWY = corner.y;
      if (corner.y > maxWY) maxWY = corner.y;
    }

    minWX -= renderPaddingTiles;
    maxWX += renderPaddingTiles;
    minWY -= renderPaddingTiles;
    maxWY += renderPaddingTiles;

    const startX = Math.floor(minWX);
    const endX = Math.ceil(maxWX);
    const startY = Math.floor(minWY);
    const endY = Math.ceil(maxWY);

    const last = this._lastGroundBounds;
    const sameBounds =
      last &&
      last.startX === startX &&
      last.endX === endX &&
      last.startY === startY &&
      last.endY === endY;
    if (!this._groundDirty && sameBounds) {
      return;
    }

    this._groundDirty = false;
    this._lastGroundBounds = { startX, endX, startY, endY };
    this._groundRebuildCounter += 1;
    const runTileEviction = (this._groundRebuildCounter & 1) === 0;
    this._usedBiomeBlendTextures.clear();

    const hw = TILE_WIDTH / 2;
    const hh = TILE_HEIGHT / 2;
    const terrainCtx = this._worldTerrainContext;
    const usedTileSprites = this._usedTileSprites;
    const usedObjectSprites = this._usedObjectSprites;
    usedTileSprites.clear();
    usedObjectSprites.clear();
    const tileMinX = qcx - (halfW + hw);
    const tileMaxX = qcx + (halfW + hw);
    const tileMinY = qcy - (halfH + hh);
    const tileMaxY = qcy + (halfH + hh);

    // Pass 2 needs slightly expanded bounds for oversized props.
    const maximumPropExtent = this._maximumPropExtent ??= Math.max(TILE_WIDTH * 4, ...manifestUseKeys(this._spriteRegistry.manifest)
      .filter(key => key.startsWith('prop.'))
      .map(key => {
        const entry = resolveManifestTexture(this._spriteRegistry.manifest, key);
        const p = entry?.presentation;
        if (!p) return MAX_SPRITE_PRESENTATION_EXTENT_PX;
        const body = p.bodyBoundsPx, contact = p.groundContactPx;
        const scale = p.displayHeightPx / body.height;
        // Only opaque body bounds can intersect the viewport; PNG decoding is unnecessary.
        const extent = Math.max(Math.abs(body.x - contact.x), Math.abs(body.x + body.width - contact.x),
          Math.abs(body.y - contact.y), Math.abs(body.y + body.height - contact.y)) * scale;
        return Number.isFinite(extent) && extent > 0 ? Math.min(extent, MAX_SPRITE_PRESENTATION_EXTENT_PX)
          : MAX_SPRITE_PRESENTATION_EXTENT_PX;
      }));
    // Legacy texture dimensions must also never create an unbounded candidate allocation.
    const safePropExtent = Number.isFinite(maximumPropExtent)
      ? Math.min(maximumPropExtent, MAX_SPRITE_PRESENTATION_EXTENT_PX)
      : TILE_WIDTH * 4;
    const objectPaddingTiles = Math.ceil(safePropExtent / TILE_HEIGHT);
    let objectStartX = startX - objectPaddingTiles;
    let objectEndX = endX + objectPaddingTiles;
    let objectStartY = startY - objectPaddingTiles;
    let objectEndY = endY + objectPaddingTiles;
    const terrainAt = (wx: number, wy: number): string =>
      this._terrainCache.getOrCreate(`${wx},${wy}`, () => proceduralTerrain(wx, wy, terrainCtx));

    const profile = this._spriteRegistry.manifest?.worldPresentation;
    const roadTexture = profile?.texturedRoads === true ? this._spriteRegistry.getTexture('terrain.road.pattern') : null;
    const useTexturedRoads = !!roadTexture;
    this._roadRibbon.clear();
    if (roadTexture || stride !== 1) {
      const bounds = { minX: tileMinX, maxX: tileMaxX, minY: tileMinY, maxY: tileMaxY };
      for (const path of presentationStyledRoadPaths(terrainCtx)) {
        for (const segment of visibleRoadSegments(path.points, bounds)) {
          // Feather into the surrounding biome without a diamond staircase or opaque shoulder tiles.
          for (const [width, alpha] of roadRibbonLayers(path.kind)) {
            const polygon = roadRibbonPolygon(segment, width);
            if (polygon.length) this._roadRibbon.poly(polygon).fill(roadTexture
              ? { texture: roadTexture, textureSpace: 'global', alpha } : { color: 0x927650, alpha });
          }
        }
      }
    }

    // Pass 1: exact close-up cells or bounded-resolution overview chunks.
    if (stride !== 1) this._drawOverviewGround(camera, screenWidth, screenHeight);
    else for (let wx = startX; wx <= endX; wx++) {
      let isoX = (wx - startY) * hw;
      let isoY = (wx + startY) * hh;
      for (let wy = startY; wy <= endY; wy++) {
        if (isoX < tileMinX || isoX > tileMaxX || isoY < tileMinY || isoY > tileMaxY) {
          isoX -= hw;
          isoY += hh;
          continue;
        }
        const originalTerrain = terrainAt(wx, wy);
        const terrain = this._terrainDisplayCache.getOrCreate(`${wx},${wy}`, () => profile?.coherentTerrain === true
          ? coherentTerrain(wx, wy, terrainCtx, originalTerrain, useTexturedRoads) : originalTerrain);
        const baseTextureKey = terrainToTextureKey(terrain);
        const floorTextureKey = this._floorTextureKey(baseTextureKey, wx, wy);
        const floorType = floorTextureKey !== null ? this._floorType(wx, wy, baseTextureKey) : null;
        const textureKey = floorTextureKey ?? baseTextureKey;
        const blendTexture = floorType === null ? null : this._biomeFloorBlendTexture(baseTextureKey, floorType,
          this._biomeFloorNeighbors(wx, wy, 1, baseTextureKey, floorType, false, useTexturedRoads), wx, wy, 1);
        const texture = blendTexture ?? this._spriteRegistry.getTexture(textureKey);
        if (texture) {
          const poolKey = `${wx},${wy}`;
          usedTileSprites.add(poolKey);
          const entry = this._ensureSprite(
            this._tileSpritePool,
            poolKey,
            textureKey,
            texture,
            this._groundSpriteLayer,
            wx,
            wy,
            this._tileSpriteFreeList,
          );
          entry.sprite.x = isoX;
          entry.sprite.y = isoY + hh;
          entry.sprite.zIndex = isoY + hh;
          const texW = entry.sprite.texture.orig.width || entry.sprite.texture.width || TILE_WIDTH;
          const flat = resolveManifestTexture(this._spriteRegistry.manifest, textureKey)?.flatTile === true;
          // Keep shared floor edges on the same subpixel grid after camera zoom.
          // Rounding each tile sprite independently can open dark gaps between neighbors.
          entry.sprite.roundPixels = !flat;
          const bleed = flat ? FLAT_TERRAIN_SEAM_BLEED_SCALE : TERRAIN_SEAM_BLEED_SCALE;
          const scale = (TILE_WIDTH / texW) * bleed;
          entry.sprite.anchor.set(0.5, flat ? 0.5 : 1);
          entry.sprite.y = isoY + (flat ? 0 : hh);
          const mirror = floorType !== null && !blendTexture ? biomeFloorMirror(wx, wy) : null;
          entry.sprite.scale.set(scale * (mirror?.x ? -1 : 1), (flat ? (TILE_HEIGHT / texture.height) * bleed : scale) * (mirror?.y ? -1 : 1));
          entry.sprite.visible = true;

          if (floorType !== null && !blendTexture) {
            const neighbors: readonly { dx: number; dy: number; edge: BiomeTileEdge }[] = [
              { dx: 1, dy: 0, edge: 'se' }, { dx: 0, dy: 1, edge: 'sw' },
              { dx: -1, dy: 0, edge: 'nw' }, { dx: 0, dy: -1, edge: 'ne' },
            ];
            for (const neighbor of neighbors) {
              const nx = wx + neighbor.dx, ny = wy + neighbor.dy;
              const original = terrainAt(nx, ny);
              const adjacent = profile?.coherentTerrain === true
                ? coherentTerrain(nx, ny, terrainCtx, original, useTexturedRoads) : original;
              if (terrainToTextureKey(adjacent) !== baseTextureKey) continue;
              const nextType = this._floorType(nx, ny, baseTextureKey);
              const transitionKey = biomeFloorTransitionTextureKey(floorType, nextType, neighbor.edge, baseTextureKey);
              if (!transitionKey) continue;
              const transition = this._spriteRegistry.getTexture(transitionKey);
              if (!transition) continue;
              const transitionMirror = biomeFloorMirror(nx, ny);
              const localEdge = biomeTileLocalEdge(neighbor.edge, transitionMirror);
              const atlasKey = biomeFloorTransitionTextureKey(floorType, nextType, localEdge, baseTextureKey);
              if (!atlasKey) continue;
              const overlay = atlasKey === transitionKey ? transition : this._spriteRegistry.getTexture(atlasKey);
              if (!overlay) continue;
              const transitionPoolKey = `${poolKey}:transition:${neighbor.edge}`;
              usedTileSprites.add(transitionPoolKey);
              const overlayEntry = this._ensureSprite(this._tileSpritePool, transitionPoolKey, atlasKey,
                overlay, this._groundSpriteLayer, wx, wy, this._tileSpriteFreeList);
              overlayEntry.sprite.roundPixels = false;
              overlayEntry.sprite.anchor.set(0.5, 0.5);
              overlayEntry.sprite.position.set(isoX, isoY);
              overlayEntry.sprite.zIndex = isoY + hh + 0.01;
              const overlayScale = TILE_WIDTH / (overlay.orig.width || overlay.width);
              overlayEntry.sprite.scale.set(overlayScale * (transitionMirror.x ? -1 : 1),
                (TILE_HEIGHT / overlay.height) * (transitionMirror.y ? -1 : 1));
              overlayEntry.sprite.visible = true;
            }
          }
        }
        isoX -= hw;
        isoY += hh;
      }
    }

    // Far static props are assembled after current-frame actors/buildings in sortEntities.
    if (this._farPropView) {
      // Keep this rebuild's working set, but retire blends from previous views
      // even though far props bypass the remainder of the near-ground pass.
      this._trimBiomeBlendTextures();
      return;
    }

    // Pass 2: objects (drawn after tiles so they layer on top)
    // Slightly expanded object bounds prevent enlarged props from edge clipping.
    const objectQuantum = stride === 1 ? 0 : TILE_WIDTH * stride / 8;
    const objectCx = objectQuantum ? Math.round(cx / objectQuantum) * objectQuantum : qcx;
    const objectCy = objectQuantum ? Math.round(cy / objectQuantum) * objectQuantum : qcy;
    const objectMinX = objectCx - (halfW + TILE_WIDTH * 1.5 + objectQuantum);
    const objectMaxX = objectCx + (halfW + TILE_WIDTH * 1.5 + objectQuantum);
    const objectMinY = objectCy - (halfH + TILE_HEIGHT * 2 + objectQuantum);
    const objectMaxY = objectCy + (halfH + TILE_HEIGHT * 2 + objectQuantum);
    const objectViewKey = `${objectCx}:${objectCy}:${screenWidth}:${screenHeight}:${camera.zoom ?? MAP_ZOOM}`;
    const updateObjects = stride === 1 || objectViewKey !== this._lastObjectViewKey;
    if (stride !== 1) {
      const objectCorners = [screenToWorld(objectMinX - safePropExtent, objectMinY - safePropExtent),
        screenToWorld(objectMaxX + safePropExtent, objectMinY - safePropExtent),
        screenToWorld(objectMinX - safePropExtent, objectMaxY + safePropExtent),
        screenToWorld(objectMaxX + safePropExtent, objectMaxY + safePropExtent)];
      objectStartX = Math.floor(Math.min(...objectCorners.map(p => p.x)));
      objectEndX = Math.ceil(Math.max(...objectCorners.map(p => p.x)));
      objectStartY = Math.floor(Math.min(...objectCorners.map(p => p.y)));
      objectEndY = Math.ceil(Math.max(...objectCorners.map(p => p.y)));
    }
    if (updateObjects) {
      this._lastObjectViewKey = objectViewKey;
      const candidates = this._queryStaticProps(objectStartX, objectEndX, objectStartY, objectEndY,
        (minX, minY, maxX, maxY) => {
          const left = (minX - maxY) * hw, right = (maxX - minY) * hw;
          const top = (minX + minY) * hh, bottom = (maxX + maxY) * hh;
          return right + safePropExtent >= objectMinX && left - safePropExtent <= objectMaxX
            && bottom + safePropExtent >= objectMinY && top - safePropExtent <= objectMaxY;
        }, { minX: objectMinX, maxX: objectMaxX, minY: objectMinY, maxY: objectMaxY });
      for (const candidate of candidates) {
        const { poolKey } = candidate;
        if (candidate.right < objectMinX || candidate.left > objectMaxX
          || candidate.bottom < objectMinY || candidate.top > objectMaxY) continue;
        usedObjectSprites.add(poolKey);
        this._placeProceduralProp(candidate, revision);
      }
    }

    const hideSpritesNow = performance.now();
    this._hideUnusedSprites(
      this._tileSpritePool,
      usedTileSprites,
      hideSpritesNow,
      this._tileSpriteFreeList,
      2800,
    );
    if (updateObjects) this._hideUnusedSprites(
      this._objectSpritePool,
      usedObjectSprites,
      hideSpritesNow,
      this._objectSpriteFreeList,
      1800,
    );
    this._trimBiomeBlendTextures();
    if (runTileEviction) {
      const evictPaddingTiles = 8;
      this._evictSpritesOutsideTileBounds(
        this._tileSpritePool,
        startX - evictPaddingTiles,
        endX + evictPaddingTiles,
        startY - evictPaddingTiles,
        endY + evictPaddingTiles,
        this._tileSpriteFreeList,
        2800,
      );
      if (updateObjects) this._evictSpritesOutsideTileBounds(
        this._objectSpritePool,
        objectStartX - evictPaddingTiles,
        objectEndX + evictPaddingTiles,
        objectStartY - evictPaddingTiles,
        objectEndY + evictPaddingTiles,
        this._objectSpriteFreeList,
        1800,
      );
    }
  }

  private _createObjectCandidate(wx: number, wy: number, obj: string, variant: number,
    poolKey: string, authoritativeTexture?: string): FarProp | null {
    const textureKey = authoritativeTexture ?? biomeObjectTextureKey(objectToTextureKey(obj, variant), obj, variant,
      wx, wy, this._worldTerrainContext, this._spriteRegistry.manifest?.worldPresentation?.biomeObjects,
      key => resolveManifestTexture(this._spriteRegistry.manifest, key) !== undefined);
    const texture = textureKey ? this._spriteRegistry.getTexture(textureKey) : null;
    if (!textureKey || !texture) return null;
    const p = this._cachedPresentation(textureKey, texture);
    const isoX = (wx - wy) * TILE_WIDTH / 2, isoY = (wx + wy) * TILE_HEIGHT / 2;
    const scale = p?.scale ?? 1, anchorX = p?.anchorX ?? .5, anchorY = p?.anchorY ?? 1;
    const width = texture.width * scale, height = texture.height * scale;
    const left = isoX - anchorX * width, top = isoY - anchorY * height;
    const body = p ? resolveManifestTexture(this._spriteRegistry.manifest, textureKey)?.presentation?.bodyBoundsPx : undefined;
    const bodyLeft = left + (body?.x ?? 0) * scale, bodyTop = top + (body?.y ?? 0) * scale;
    const bodyRect = { left: bodyLeft, top: bodyTop, right: bodyLeft + (body?.width ?? texture.width) * scale,
      bottom: bodyTop + (body?.height ?? texture.height) * scale };
    return { wx, wy, textureKey, texture, isoX, isoY, poolKey, anchorX, anchorY, scale,
      left, right: left + width, top, bottom: top + height,
      bodyRect };
  }

  /** The same exact object list feeds individual sprites and far texture baking. */
  private _sceneCandidates(scene: RuntimeScene): readonly FarProp[] {
    return this._sceneProps.getOrCreate(scene.id, () => scene.objects.flatMap(object => {
      const candidate = this._createObjectCandidate(object.worldX, object.worldY,
        object.objectType, object.variant, object.id);
      return candidate ? [candidate] : [];
    }));
  }

  /** Full logical PNG bounds match _createObjectCandidate; body-only bounds could hide shadows. */
  private _authoritativePropIntersectsBounds(wx: number, wy: number, key: string, bounds: ScreenBounds): boolean {
    const manifest = this._spriteRegistry.manifest;
    const entry = resolveManifestTexture(manifest, key);
    const dimensions = entry && manifest?.sourceDimensions?.[entry.file];
    const width = entry?.frame?.width ?? dimensions?.width;
    const height = entry?.frame?.height ?? dimensions?.height;
    // Older manifests and malformed optional metadata retain conservative loading.
    if (typeof width !== 'number' || typeof height !== 'number' || !Number.isFinite(width)
      || !Number.isFinite(height) || width <= 0 || height <= 0) return true;
    const p = spritePresentation(entry, width, height);
    return spriteIntersectsViewport((wx - wy) * TILE_WIDTH / 2, (wx + wy) * TILE_HEIGHT / 2,
      width, height, p?.scale ?? 1, p?.anchorX ?? .5, p?.anchorY ?? 1,
      bounds.minX, bounds.maxX, bounds.minY, bounds.maxY);
  }

  private *_queryStaticProps(startX: number, endX: number, startY: number, endY: number,
    intersects: (minX: number, minY: number, maxX: number, maxY: number) => boolean, bounds?: ScreenBounds): IterableIterator<FarProp> {
    const chunks = this._worldTerrainContext?.authoritativeScenery;
    if (chunks !== undefined) {
      // chunk.objects already contains ambient + scene props; scene.objects is metadata.
      // Missing chunks remain empty rather than inventing colliders the server never authored.
      const seen = new Set<string>();
      for (const chunk of chunks) {
        const minX = chunk.chunkX * 64, minY = chunk.chunkY * 64;
        if (minX > endX || minX + 64 < startX || minY > endY || minY + 64 < startY || !intersects(minX,minY,minX+64,minY+64)) continue;
        // Contact bounds already include the maximum presentation extent. Filter
        // metadata before requesting pixels: a visible chunk may span many screens.
        // Do not cache this partial list by chunk, since a pan can expose other props.
        for (const object of chunk.objects) {
          if (seen.has(object.id) || object.worldX < startX || object.worldX > endX
            || object.worldY < startY || object.worldY > endY) continue;
          if (bounds && !this._authoritativePropIntersectsBounds(object.worldX, object.worldY, object.textureKey, bounds)) continue;
          const prop = this._createObjectCandidate(object.worldX,object.worldY,object.objectType,object.variant,object.id,object.textureKey);
          if (prop) { seen.add(prop.poolKey); yield prop; }
        }
      }
      return;
    }
    for (const scene of this._runtimeScenes?.query(startX, startY, endX, endY) ?? []) {
      yield* this._sceneCandidates(scene);
    }
  }

  private _queryPropCandidates(bounds: ScreenBounds): readonly FarProp[] {
    const extent = Math.min(this._maximumPropExtent ?? TILE_WIDTH * 4, MAX_SPRITE_PRESENTATION_EXTENT_PX);
    const corners = [screenToWorld(bounds.minX - extent, bounds.minY - extent),
      screenToWorld(bounds.maxX + extent, bounds.minY - extent),
      screenToWorld(bounds.minX - extent, bounds.maxY + extent),
      screenToWorld(bounds.maxX + extent, bounds.maxY + extent)];
    const candidates = this._queryStaticProps(Math.floor(Math.min(...corners.map(p => p.x))),
      Math.ceil(Math.max(...corners.map(p => p.x))), Math.floor(Math.min(...corners.map(p => p.y))),
      Math.ceil(Math.max(...corners.map(p => p.y))),
      (minX,minY,maxX,maxY) => (maxX-minY)*TILE_WIDTH/2+extent >= bounds.minX
        && (minX-maxY)*TILE_WIDTH/2-extent <= bounds.maxX
        && (maxX+maxY)*TILE_HEIGHT/2+extent >= bounds.minY
        && (minX+minY)*TILE_HEIGHT/2-extent <= bounds.maxY, bounds);
    return [...candidates].filter(p => p.left <= bounds.maxX && p.right >= bounds.minX
      && p.top <= bounds.maxY && p.bottom >= bounds.minY);
  }

  private _placeProceduralProp(candidate: FarProp, revision: number | undefined): void {
    const { wx, wy, textureKey, texture, poolKey, isoX, isoY } = candidate;
    let entry = this._objectSpritePool.get(poolKey);
    if (!entry || entry.textureKey !== textureKey || entry.sprite.texture !== texture || entry.presentationRevision !== revision) {
      entry = this._ensureSprite(this._objectSpritePool, poolKey, textureKey, texture,
        this.entityLayer, wx, wy, this._objectSpriteFreeList);
      entry.sprite.position.set(isoX, isoY); entry.sprite.zIndex = isoY;
      this._applyPresentation(entry, 1); entry.presentationRevision = revision; entry.bodyRect = candidate.bodyRect;
    }
    entry.sprite.visible = true;
    if (entry.sprite.parent !== this.entityLayer) this.entityLayer.addChild(entry.sprite);
  }

  private _externalArtBounds(child: Container): ScreenBounds {
    if (child instanceof Sprite && child.rotation === 0 && child.skew.x === 0 && child.skew.y === 0) {
      const width = child.texture.orig.width, height = child.texture.orig.height;
      const a = child.x - child.anchor.x * width * child.scale.x, b = a + width * child.scale.x;
      const c = child.y - child.anchor.y * height * child.scale.y, d = c + height * child.scale.y;
      return { minX: Math.min(a,b), maxX: Math.max(a,b), minY: Math.min(c,d), maxY: Math.max(c,d) };
    }
    child.updateLocalTransform();
    const rect = child.getLocalBounds().rectangle, m = child.localTransform;
    const points = [[rect.x,rect.y],[rect.x+rect.width,rect.y],[rect.x,rect.y+rect.height],
      [rect.x+rect.width,rect.y+rect.height]].map(([x,y]) => ({ x: m.a*x!+m.c*y!+m.tx, y: m.b*x!+m.d*y!+m.ty }));
    return { minX: Math.min(...points.map(p=>p.x)), maxX: Math.max(...points.map(p=>p.x)),
      minY: Math.min(...points.map(p=>p.y)), maxY: Math.max(...points.map(p=>p.y)) };
  }

  private _drawFarProps(): void {
    const view = this._farPropView;
    if (!view) return;
    const excluded = new Set<string>(), hero = this._characterSpritePool.get('hero');
    if (hero?.sprite.visible && hero.sprite.alpha > 0) {
      const camp = this._visibleRestCamp().filter(entry=>entry.sprite.alpha > 0 && !entry.sprite.destroyed);
      const walkBody = spriteBodyRect(hero.sprite, hero.textureKey.startsWith('hero.whole.')
        ? this._heroPresentation : resolveManifestTexture(this._spriteRegistry.manifest, hero.textureKey));
      const viewport = { left: view.camera.finalX - view.width / (2*(view.camera.zoom ?? 1)),
        right: view.camera.finalX + view.width / (2*(view.camera.zoom ?? 1)),
        top: view.camera.finalY - view.height / (2*(view.camera.zoom ?? 1)),
        bottom: view.camera.finalY + view.height / (2*(view.camera.zoom ?? 1)) };
      const targets = ownedRevealTargets(hero.sprite, walkBody, camp.map(entry => ({ sprite: entry.sprite,
        body: spriteBodyRect(entry.sprite, resolveManifestTexture(this._spriteRegistry.manifest, entry.textureKey)) })), viewport);
      if (targets.length) {
        const campVisible = targets.some(target=>target.sprite !== hero.sprite);
        const protectedCamp = campVisible ? campRevealTargets(hero.sprite,targets) : [];
        const bounds = heroRevealBounds(hero.sprite,campVisible ? campRevealEllipse(hero.sprite) : heroRevealEllipse(hero.sprite,walkBody));
        for (const p of this._queryPropCandidates({ minX: bounds.left, maxX: bounds.right, minY: bounds.top, maxY: bounds.bottom })) {
          const front = p.isoY > hero.sprite.zIndex || p.isoY === hero.sprite.zIndex && p.poolKey > hero.sprite.label;
          const intersects = campVisible ? ownedOccludedTargets(protectedCamp,{zIndex:p.isoY,label:p.poolKey},bounds).length > 0 && rectanglesOverlap(p.bodyRect,bounds)
            : front && rectanglesOverlap(p.bodyRect,bounds);
          if (p.bodyRect.bottom-p.bodyRect.top >= 60 && intersects) excluded.add(p.poolKey);
        }
      }
    }
    const external: PropDepthCut[] = [];
    for (const child of this.entityLayer.children) {
      if (!child.visible || child.alpha <= 0 || this._farProps.owns(child)
        || this._objectSpritePool.has(child.label)) continue;
      const bounds = this._externalArtBounds(child);
      if (bounds.maxX > bounds.minX && bounds.maxY > bounds.minY)
        external.push({ label: child.label, zIndex: child.zIndex, bounds });
    }
    const individual = this._farProps.update(view.camera,view.width,view.height,view.stride,external,excluded);
    const used = this._usedObjectSprites; used.clear();
    for (const p of individual) { used.add(p.poolKey); this._placeProceduralProp(p,this._spriteRegistry.revision); }
    // Cached far art replaces the original static sprites; detach rather than sorting thousands of hidden children.
    for (const [key,entry] of this._objectSpritePool) if (!used.has(key)) {
      this._objectSpritePool.delete(key); entry.sprite.visible = false; entry.sprite.texture = Texture.EMPTY; entry.sprite.removeFromParent();
      if (this._objectSpriteFreeList.length < 1800) this._objectSpriteFreeList.push(entry.sprite);
      else entry.sprite.destroy();
    }
    for (const sprite of this._objectSpriteFreeList) sprite.removeFromParent();
  }

  private _drawOverviewGround(camera: CameraView, width: number, height: number): void {
    const visibleSprites = new Set<Sprite>();
    const visible = new Set<string>();
    for (const chunk of overviewChunks(camera, width, height)) {
      visible.add(chunk.key);
      const entry = this._overviewChunks.getOrCreate(chunk.key, () => this._buildGroundChunk(chunk));
      visibleSprites.add(entry.sprite);
      entry.sprite.visible = true;
    }
    for (const entry of this._overviewChunks.values()) if (!visibleSprites.has(entry.sprite)) entry.sprite.visible = false;
    this._overviewChunks.trim(visible);
    // Overview outputs are independent baked textures; their cell blends are no
    // longer referenced once generateTexture returns. Keep only a small reuse window.
    this._usedBiomeBlendTextures.clear();
    this._trimBiomeBlendTextures(96);
  }

  private _buildGroundChunk(chunk: GroundChunk): { sprite: Sprite; texture: Texture; incomplete: boolean } {
    const { x, y, size, stride, resolution } = chunk;
    // Two physical pixels of neighboring artwork avoid linear-sampling seams at chunk edges.
    const gutter = 2 / resolution;
    const corners = [screenToWorld(x - gutter, y - gutter), screenToWorld(x + size + gutter, y - gutter),
      screenToWorld(x - gutter, y + size + gutter), screenToWorld(x + size + gutter, y + size + gutter)];
    const minX = Math.floor(Math.min(...corners.map(p => p.x)) / stride) * stride - stride * 2;
    const maxX = Math.ceil(Math.max(...corners.map(p => p.x)) / stride) * stride + stride * 2;
    const minY = Math.floor(Math.min(...corners.map(p => p.y)) / stride) * stride - stride * 2;
    const maxY = Math.ceil(Math.max(...corners.map(p => p.y)) / stride) * stride + stride * 2;
    const scene = new Container(); scene.sortableChildren = true;
    let incomplete = false;
    try {
      for (let wx = minX; wx <= maxX; wx += stride) for (let wy = minY; wy <= maxY; wy += stride) {
        const point = worldToScreen(wx, wy);
        if (point.x + TILE_WIDTH * stride < x - gutter || point.x - TILE_WIDTH * stride > x + size + gutter
          || point.y + TILE_HEIGHT * stride < y - gutter || point.y - TILE_HEIGHT * stride > y + size + gutter) continue;
        this._groundChunkSamples++;
        const original = proceduralTerrain(wx, wy, this._worldTerrainContext);
        const profile = this._spriteRegistry.manifest?.worldPresentation;
        // Roads retain their exact vector centerline rather than turning into coarse disconnected cells.
        const terrain = profile?.coherentTerrain === true || original === 'road'
          ? coherentTerrain(wx, wy, this._worldTerrainContext, original, true) : original;
        const baseKey = terrainToTextureKey(terrain);
        const floorTextureKey = this._floorTextureKey(baseKey, wx, wy);
        const floorType = floorTextureKey !== null ? this._floorType(wx, wy, baseKey) : null;
        const key = floorTextureKey ?? baseKey;
        const neighbors = floorType === null ? null : this._biomeFloorNeighbors(wx, wy, stride, baseKey, floorType, true, true);
        const blendTexture = floorType === null ? null : this._biomeFloorBlendTexture(baseKey, floorType,
          neighbors!, wx, wy, stride);
        const texture = blendTexture ?? this._spriteRegistry.getTexture(key);
        if (floorType !== null && neighbors?.some(type => type !== floorType) && !blendTexture) incomplete = true;
        if (!texture) {
          incomplete = true;
          const halfWidth = TILE_WIDTH * stride / 2 * FLAT_TERRAIN_SEAM_BLEED_SCALE;
          const halfHeight = TILE_HEIGHT * stride / 2 * FLAT_TERRAIN_SEAM_BLEED_SCALE;
          const fallback = new Graphics().poly([point.x, point.y - halfHeight, point.x + halfWidth, point.y,
            point.x, point.y + halfHeight, point.x - halfWidth, point.y]).fill({ color: fallbackGroundColor(terrain) });
          fallback.zIndex = point.y;
          scene.addChild(fallback);
          continue;
        }
        const sprite = new Sprite(texture);
        const flat = resolveManifestTexture(this._spriteRegistry.manifest, key)?.flatTile === true;
        // Keep at least two raster pixels of overlap after low-resolution chunk rendering.
        const bleed = flat
          ? Math.max(FLAT_TERRAIN_SEAM_BLEED_SCALE, 1 + 2 / (TILE_HEIGHT * stride * resolution))
          : TERRAIN_SEAM_BLEED_SCALE;
        const scale = TILE_WIDTH * stride / (texture.orig.width || texture.width) * bleed;
        sprite.anchor.set(.5, flat ? .5 : 1);
        sprite.position.set(point.x, point.y + (flat ? 0 : TILE_HEIGHT * stride / 2));
        const mirror = floorType !== null && !blendTexture ? biomeFloorMirror(wx, wy) : null;
        sprite.scale.set(scale * (mirror?.x ? -1 : 1), (flat ? TILE_HEIGHT * stride / texture.height * bleed : scale) * (mirror?.y ? -1 : 1));
        sprite.zIndex = point.y;
        scene.addChild(sprite);
        if (floorType !== null && !blendTexture) {
          const neighbors: readonly { dx: number; dy: number; edge: BiomeTileEdge }[] = [
            { dx: 1, dy: 0, edge: 'se' }, { dx: 0, dy: 1, edge: 'sw' },
            { dx: -1, dy: 0, edge: 'nw' }, { dx: 0, dy: -1, edge: 'ne' },
          ];
          for (const neighbor of neighbors) {
            const nx = wx + neighbor.dx * stride, ny = wy + neighbor.dy * stride;
            const neighborOriginal = proceduralTerrain(nx, ny, this._worldTerrainContext);
            const neighborTerrain = profile?.coherentTerrain === true || neighborOriginal === 'road'
              ? coherentTerrain(nx, ny, this._worldTerrainContext, neighborOriginal, true) : neighborOriginal;
            if (terrainToTextureKey(neighborTerrain) !== baseKey) continue;
            const nextType = this._floorType(nx, ny, baseKey);
            const rawKey = biomeFloorTransitionTextureKey(floorType, nextType, neighbor.edge, baseKey);
            if (!rawKey) continue;
            const transitionMirror = biomeFloorMirror(nx, ny);
            const transitionKey = biomeFloorTransitionTextureKey(floorType, nextType,
              biomeTileLocalEdge(neighbor.edge, transitionMirror), baseKey);
            if (!transitionKey) continue;
            const overlay = this._spriteRegistry.getTexture(transitionKey);
            if (!overlay) { incomplete = true; continue; }
            const overlaySprite = new Sprite(overlay);
            const overlayScale = TILE_WIDTH * stride / (overlay.orig.width || overlay.width);
            overlaySprite.anchor.set(.5, .5);
            overlaySprite.position.set(point.x, point.y);
            overlaySprite.scale.set(overlayScale * (transitionMirror.x ? -1 : 1),
              TILE_HEIGHT * stride / overlay.height * (transitionMirror.y ? -1 : 1));
            overlaySprite.zIndex = point.y + 0.01;
            scene.addChild(overlaySprite);
          }
        }
      }
      const texture = this.app.renderer.generateTexture({ target: scene,
        frame: new Rectangle(x - gutter, y - gutter, size + gutter * 2, size + gutter * 2), resolution });
      texture.source.scaleMode = 'linear';
      const view = new Texture({ source: texture.source, frame: new Rectangle(gutter, gutter, size, size) });
      const sprite = new Sprite(view); sprite.label = `ground-chunk:${chunk.key}`;
      sprite.position.set(x, y); this._overviewLayer.addChild(sprite);
      this._groundChunkBuilds++;
      return { sprite, texture, incomplete };
    } finally {
      scene.destroy({ children: true });
    }
  }

  /**
   * Draw the hero (sprite only).
   */
  drawHero(wx: number, wy: number, _phase: 'walk' | 'fight' | 'idle', _now: number, modelVariant: number, facing: HeroFacing = 'south', motion?: HeroAnimationView, heroId = 0, resting = false): void {
    const gfx = this._heroGfx;
    if (!gfx) return;
    const iso = worldToScreen(wx, wy);
    const heroFacing = motion ? motion.facing : facing;
    let textureKey = heroTextureKey(modelVariant, heroFacing);
    let texture = this._spriteRegistry.getTexture(textureKey);
    const art = resolveManifestTexture(this._spriteRegistry.manifest, textureKey);
    const staticArt = modelVariant === HERO_ANIMATION.modelVariant && motion && texture && spritePresentation(art, texture.width, texture.height)
      ? art?.presentation : undefined;
    if (modelVariant === HERO_ANIMATION.modelVariant && motion && !staticArt) {
      textureKey = heroTextureKey(modelVariant, facing); texture = this._spriteRegistry.getTexture(textureKey);
    }

    let animated: Texture | null = null;
    this._heroPresentation = undefined;
    const candidateClip = motion?.clip ?? (_phase === 'fight' ? 'combat-idle' : 'idle');
    const candidate = this._heroAnimationAssets.definition(modelVariant, candidateClip, heroFacing);
    if (candidate) {
      const frames = this._heroAnimationAssets.get(candidateClip, heroFacing, { width: candidate.width, height: candidate.height }, modelVariant);
      if (frames) animated = wholeAnimationFrame(frames, motion ?? { clip: candidateClip, facing: heroFacing, frame: 0 }, candidate);
      if (animated) this._heroPresentation = { file: candidate.file, kind: 'character', presentation: candidate.presentation };
    }
    if (!candidate && modelVariant === HERO_ANIMATION.modelVariant && motion && texture && staticArt) {
      const size = { width: texture.width, height: texture.height };
      const fighting = _phase === 'fight' && motion.clip !== 'idle';
      const clip = fighting ? motion.clip === 'walk' ? 'combat-idle' : motion.clip : motion.clip === 'walk' ? 'walk' : null;
      if (clip) {
        const frames = this._heroAnimationAssets.get(clip, motion.facing, size, -1)
          ?? (fighting && clip !== 'combat-idle' ? this._heroAnimationAssets.get('combat-idle', motion.facing, size, -1) : null);
        if (frames) animated = wholeAnimationFrame(frames, motion);
        if (animated) this._heroPresentation = wholeAnimationPresentation(staticArt, motion.facing, fighting);
      }
    }
    let idleTexture: Texture | null = null;
    const heroIdleCharacter = stableHeroIdleCharacter(modelVariant);
    if (!candidate && !resting && _phase === 'idle' && (!motion || motion.clip === 'idle')) {
      const idle = heroIdleCharacter && individualIdleFrame(_now, `hero:${heroId}`, heroIdleCharacter, false);
      const frames = idle && this._individualIdleAssets.get(idle.character.character, idle.clip);
      if (idle && frames) idleTexture = frames[individualIdleTextureIndex(idle.character, idle.clip, idle.frame)] ?? null;
    } else if (heroIdleCharacter) individualIdleFrame(_now, `hero:${heroId}`, heroIdleCharacter, true);
    if (idleTexture) { animated = idleTexture; this._heroPresentation = undefined; }
    if (animated) texture = animated;

    if (texture) {
      const entry = this._ensureSprite(
        this._characterSpritePool,
        'hero',
        animated ? `hero.whole.${heroFacing}` : textureKey,
        texture,
        this.entityLayer,
      );
      entry.sprite.x = iso.x;
      entry.sprite.y = iso.y;
      entry.sprite.texture = texture;
      entry.sprite.anchor.set(0.5, 1);
      if (this._heroPresentation) {
        const p = spritePresentation(this._heroPresentation, texture.width, texture.height)!;
        entry.sprite.anchor.set(p.anchorX, p.anchorY); entry.sprite.scale.set(p.scale);
      } else if (idleTexture) { entry.sprite.anchor.set(0.5, 0.86); entry.sprite.scale.set(1); }
      else this._applyPresentation(entry, characterSpriteScale(texture.height));
      // Each rendered pose is one whole image; deformation and layers stay disabled.
      entry.sprite.renderable = true;
      entry.sprite.zIndex = iso.y;
      entry.sprite.visible = true;
    } else {
      gfx.clear();
      const entry = this._characterSpritePool.get('hero');
      if (entry) entry.sprite.visible = false;
    }

    const nameTxt = this._heroNameText;
    if (nameTxt && this._heroName) {
      nameTxt.text = this._heroName;
      nameTxt.x = iso.x;
      nameTxt.y = iso.y - CHARACTER_DISPLAY_HEIGHT_PX - 2;
      nameTxt.visible = true;
      nameTxt.zIndex = 20;
    }
  }

  /**
   * Meet opponent: same figure as the main hero, idle stance, distinct tint + name label.
   */
  drawMeetPartner(wx: number, wy: number, name: string, level: number, _now: number, modelVariant: number): void {
    const gfx = this._meetPartnerGfx;
    const lbl = this._meetPartnerLabel;
    if (!gfx || !lbl) return;
    const iso = worldToScreen(wx, wy);
    const textureKey = heroTextureKey(modelVariant, 'north');
    const texture = this._spriteRegistry.getTexture(textureKey);

    if (!texture) {
      gfx.clear();
      const entry = this._characterSpritePool.get('meet_partner');
      if (entry) entry.sprite.visible = false;
      lbl.visible = false;
      return;
    }

    const entry = this._ensureSprite(
      this._characterSpritePool,
      'meet_partner',
      textureKey,
      texture,
      this.entityLayer,
    );
    entry.sprite.x = iso.x;
    entry.sprite.y = iso.y;
    this._applyPresentation(entry, characterSpriteScale(texture.height));
    entry.sprite.zIndex = iso.y;
    entry.sprite.visible = true;

    lbl.text = `${name} Lv.${level}`;
    lbl.x = iso.x;
    lbl.y = iso.y + 10;
    lbl.visible = true;
    lbl.zIndex = 20;
  }

  clearMeetPartner(): void {
    this._meetPartnerGfx?.clear();
    const entry = this._characterSpritePool.get('meet_partner');
    if (entry) entry.sprite.visible = false;
    if (this._meetPartnerLabel) {
      this._meetPartnerLabel.visible = false;
    }
  }

  /**
   * Wilderness rest: tent + fire + bag as separate transparent sprites around the hero (wild phase).
   */
  drawRestCamp(wx: number, wy: number, now: number): void {
    const gfx = this._restCampGfx;
    if (!gfx) return;

    const iso = worldToScreen(wx, wy);
    const seat = this._characterSpritePool.get('rest_camp_seat');
    if (seat) seat.sprite.visible = false;

    const hideRestCampSprites = (): void => {
      for (const key of ['rest_camp_tent', 'rest_camp_fire', 'rest_camp_bag', 'rest_camp_seat'] as const) {
        const e = this._characterSpritePool.get(key);
        if (e) e.sprite.visible = false;
      }
    };

    const [tentKey, fireKey, bagKey] = restCampTextureKeys();
    const tentTex = this._spriteRegistry.getTexture(tentKey);
    const fireTex = this._spriteRegistry.getTexture(fireKey);
    const bagTex = this._spriteRegistry.getTexture(bagKey);
    if (!(tentTex && fireTex && bagTex)) {
      hideRestCampSprites();
      gfx.clear();
      return;
    }

    const place = (
      poolKey: string,
      textureKey: string,
      texture: Texture,
      x: number,
      y: number,
      scale: number,
    ): void => {
      const entry = this._ensureSprite(
        this._characterSpritePool,
        poolKey,
        textureKey,
        texture,
        this.entityLayer,
      );
      entry.sprite.anchor.set(0.5, 1);
      entry.sprite.x = x;
      entry.sprite.y = y;
      this._applyPresentation(entry, scale);
      entry.sprite.zIndex = y;
      entry.sprite.visible = true;
    };
    place('rest_camp_tent', tentKey, tentTex, iso.x - 56, iso.y - 6, 2.1);
    const fireFrames = this._restIdleAssets.get('fire');
    const fireDefinition = this._restIdleAssets.definition('fire');
    const fireFrame = fireFrames && fireDefinition
      ? fireFrames[restIdleFrame(now, fireFrames.length, fireDefinition.frameMs, this._restReducedMotion())] : null;
    place('rest_camp_fire', fireKey, fireFrame ?? fireTex, iso.x + MESHY_CAMP_FIRE_OFFSET[0], iso.y + MESHY_CAMP_FIRE_OFFSET[1], 1.5);
    const fireEntry = this._characterSpritePool.get('rest_camp_fire');
    if (fireEntry) fireEntry.sprite.texture = fireFrame ?? fireTex;
    place('rest_camp_bag', bagKey, bagTex, iso.x - 42, iso.y + 26, 1.1);
  }

  private _restReducedMotion(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Replace only the local hero's whole pose; camp objects remain independent sprites. */
  private _drawRestingHero(wx: number, wy: number, variant: number, now: number): void {
    const activity = this._restActivityView?.activity ?? 'cooking';
    const candidate = this._heroAnimationAssets.definition(variant, activity, 'south-east');
    const candidateFrames = candidate ? this._heroAnimationAssets.get(activity, 'south-east', candidate, variant) : null;
    const cookingKey = restActivityAssetKey(variant, 'cooking');
    const requestedKey = restActivityAssetKey(variant, this._restActivityView?.activity ?? 'cooking');
    const cookingFrames = this._restIdleAssets.get(cookingKey);
    // Preload both later activities during the first minute; asset latency never owns the phase clock.
    this._restIdleAssets.get(restActivityAssetKey(variant, 'weapon-care'));
    this._restIdleAssets.get(restActivityAssetKey(variant, 'bandaging'));
    const requestedFrames = this._restIdleAssets.get(requestedKey);
    const frames = candidateFrames ?? restActivityFallback(requestedFrames, cookingFrames);
    const key = requestedFrames ? requestedKey : cookingKey;
    const definition = candidateFrames ? candidate : this._restIdleAssets.definition(key);
    const entry = this._characterSpritePool.get('hero');
    if (!definition?.presentation || !frames || !entry) return;
    const elapsed = this._restActivityView?.phaseElapsedMs ?? now;
    const texture = candidateFrames && candidate
      ? frames[this._restReducedMotion() ? 0 : meshyClipFrame(candidate, (elapsed % meshyClipDuration(candidate)) / meshyClipDuration(candidate))]
      : frames[restIdleFrame(elapsed, frames.length, 'frameMs' in definition ? definition.frameMs : 120, this._restReducedMotion())];
    if (!texture) return;
    const art = { file: definition.file, kind: 'character', presentation: definition.presentation };
    const presentation = spritePresentation(art, texture.width, texture.height);
    if (!presentation) return;
    const iso = worldToScreen(wx, wy);
    const seatTexture = this._spriteRegistry.getTexture('prop.stump.v0');
    if (seatTexture && this._characterSpritePool.get('rest_camp_tent')?.sprite.visible) {
      const seat = this._ensureSprite(this._characterSpritePool, 'rest_camp_seat', 'prop.stump.v0', seatTexture, this.entityLayer);
      const metadata = resolveManifestTexture(this._spriteRegistry.manifest, 'prop.stump.v0');
      const p = spritePresentation(metadata, seatTexture.width, seatTexture.height);
      seat.sprite.anchor.set(p?.anchorX ?? 0.5, p?.anchorY ?? 1);
      seat.sprite.scale.set((p?.scale ?? 1) * 0.4);
      seat.sprite.position.set(iso.x - 4, iso.y - 2);
      seat.sprite.zIndex = iso.y - 2;
      seat.sprite.visible = true;
    }
    this._heroPresentation = art;
    entry.textureKey = `hero.whole.rest.v${variant}`;
    entry.sprite.texture = texture;
    entry.sprite.anchor.set(presentation.anchorX, presentation.anchorY);
    entry.sprite.scale.set(presentation.scale);
    entry.sprite.position.set(iso.x, iso.y);
    entry.sprite.zIndex = iso.y;
    if (this._heroNameText) this._heroNameText.y = iso.y - definition.presentation.displayHeightPx - 2;
  }

  clearRestCamp(): void {
    this._restActivityClock.reset();
    this._restActivityView = null;
    if (this._restCampGfx) this._restCampGfx.clear();
    for (const key of ['rest_camp_tent', 'rest_camp_fire', 'rest_camp_bag', 'rest_camp_seat'] as const) {
      const e = this._characterSpritePool.get(key);
      if (e) e.sprite.visible = false;
    }
  }

  /**
   * Draw an enemy with type-specific visuals and an HP bar above.
   */
  drawEnemy(
    wx: number,
    wy: number,
    // hp: number,
    // maxHp: number,
    enemySlug: string,
    // enemyArchetype: string | undefined,
    now: number,
  ): void {
    const key = resolveEnemySouthTextureKey(enemySlug, (k) => resolveManifestTexture(this._spriteRegistry.manifest, k) ?? null);
    this._drawEnemyTextureKey(wx, wy, key, now);
  }

  /** Exact registered artwork for presentation fixtures; production callers retain slug resolution. */
  drawEnemyTextureKey(wx: number, wy: number, key: string, now: number): void {
    this._drawEnemyTextureKey(wx, wy, key, now);
  }

  private _drawEnemyTextureKey(wx: number, wy: number, southKey: string | null, now: number): void {
    const gfx = this._enemyGfx;
    if (!gfx) return;

    const iso = worldToScreen(wx, wy);
    const sway = Math.sin(now * 0.004) * 2;
    const cx = iso.x;
    const cy = iso.y + sway;

    const tex = southKey ? this._spriteRegistry.getTexture(southKey) : null;
    if (tex && southKey) {
      const entry = this._ensureSprite(
        this._characterSpritePool,
        'enemy_combat',
        southKey,
        tex,
        this.entityLayer,
      );
      entry.sprite.anchor.set(0.5, 1);
      entry.sprite.x = cx;
      entry.sprite.y = cy;
      entry.sprite.roundPixels = true;
      const th = Math.max(1, tex.height || tex.width || 48);
      const targetH = 100;
      this._applyPresentation(entry, targetH / th);
      entry.sprite.zIndex = iso.y;
      entry.sprite.visible = true;
      return;
    }

    gfx.clear();
    const pooled = this._characterSpritePool.get('enemy_combat');
    if (pooled) pooled.sprite.visible = false;
  }

  clearEnemyCombat(): void {
    if (this._enemyGfx) this._enemyGfx.clear();
    const pooled = this._characterSpritePool.get('enemy_combat');
    if (pooled) pooled.sprite.visible = false;
  }

  /** Set the hero display name for the above-hero label */
  setHeroName(name: string): void {
    this._heroName = name;
    if (this._heroNameText) {
      this._heroNameText.text = name;
      this._heroNameText.visible = name.length > 0;
    }
  }

  /**
   * Building-layer sprite (anchor bottom-center). Returns true if the texture was placed.
   */
  private _placeBuildingLayerSprite(
    poolKey: string,
    textureKey: string,
    bx: number,
    by: number,
    usedBuildingSprites: Set<string>,
    sizeMultiplier = 1,
  ): boolean {
    const spriteTexture = this._spriteRegistry.getTexture(textureKey);
    if (!spriteTexture) return false;
    usedBuildingSprites.add(poolKey);
    const entry = this._ensureSprite(
      this._buildingSpritePool,
      poolKey,
      textureKey,
      spriteTexture,
      this.entityLayer,
    );
    entry.sprite.anchor.set(0.5, buildingGroundAnchorY(textureKey));
    entry.sprite.x = bx;
    entry.sprite.y = by;
    this._applyPresentation(entry, 1);
    if (sizeMultiplier !== 1) entry.sprite.scale.set(entry.sprite.scale.x * sizeMultiplier);
    entry.sprite.zIndex = by;
    entry.sprite.visible = true;
    return true;
  }

  /**
   * Draw server-defined buildings (sprites only). Purpose icons only when the building sprite exists.
   */
  private _buildingIntersectsFrame(building: ReadonlyData<BuildingData>, key: string): boolean {
    const bounds = this._frameViewport, manifest = this._spriteRegistry.manifest;
    const art = resolveManifestTexture(manifest, key), dimensions = art && manifest?.sourceDimensions?.[art.file];
    const width = art?.frame?.width ?? dimensions?.width, height = art?.frame?.height ?? dimensions?.height;
    if (!bounds || typeof width !== 'number' || typeof height !== 'number' || ![width, height].every(n => Number.isFinite(n) && n > 0)) return true;
    const presentation = spritePresentation(art, width, height);
    const placement = authoredBuildingPresentation(building, width, height, presentation?.scale ?? 1, art);
    const anchorX = placement?.anchorX ?? presentation?.anchorX ?? .5;
    const anchorY = placement?.anchorY ?? presentation?.anchorY ?? buildingGroundAnchorY(key);
    const scaleX = placement?.scaleX ?? presentation?.scale ?? 1, scaleY = placement?.scaleY ?? presentation?.scale ?? 1;
    const point = worldToScreen(building.worldX, building.worldY);
    const x1 = point.x - anchorX * width * scaleX, x2 = point.x + (1 - anchorX) * width * scaleX;
    const y1 = point.y - anchorY * height * scaleY, y2 = point.y + (1 - anchorY) * height * scaleY;
    // The town draw cache tolerates a tile of camera motion between redraws.
    return Math.min(x1, x2) <= bounds.maxX + TILE_WIDTH && Math.max(x1, x2) >= bounds.minX - TILE_WIDTH
      && Math.min(y1, y2) <= bounds.maxY + TILE_HEIGHT && Math.max(y1, y2) >= bounds.minY - TILE_HEIGHT;
  }

  private _drawServerBuildings(
    iconGfx: Graphics,
    usedBuildingSprites: Set<string>,
    buildings: readonly ReadonlyData<BuildingData>[],
    scale: number,
  ): void {
    for (let i = 0; i < buildings.length; i++) {
      const b = buildings[i]!;
      const bScreen = worldToScreen(b.worldX, b.worldY);
      const bx = bScreen.x;
      const by = bScreen.y;
      const bt = b.buildingType;
      const rh = 32 * scale;
      const variantPick = resolveBuildingSpriteVariant(b.id, b.worldX, b.worldY, b.variant);
      const spriteKey = b.authored ? authoredTextureKey(b.authored.family,variantPick) : buildingTypeToTextureKey(bt, variantPick);
      if (spriteKey && !this._buildingIntersectsFrame(b, spriteKey)) continue;
      const spriteTexture = spriteKey ? this._spriteRegistry.getTexture(spriteKey) : null;
      const hasUsableSprite = spriteKey !== null && spriteTexture !== null;
      if (spriteKey !== null && spriteTexture !== null) {
        const poolKey = `building:${b.id}`;
        usedBuildingSprites.add(poolKey);
        const entry = this._ensureSprite(
          this._buildingSpritePool,
          poolKey,
          spriteKey,
          spriteTexture,
          this.entityLayer,
        );
        entry.sprite.anchor.set(0.5, buildingGroundAnchorY(spriteKey));
        entry.sprite.x = bx;
        entry.sprite.y = by;
        this._applyPresentation(entry, 1);
        entry.sprite.zIndex = by;
        if (b.authored) {
          const placement=authoredBuildingPresentation(b,spriteTexture.width,spriteTexture.height,entry.sprite.scale.x,resolveManifestTexture(this._spriteRegistry.manifest, spriteKey));
          if (placement) {
            entry.sprite.anchor.set(placement.anchorX,placement.anchorY);
            entry.sprite.scale.set(placement.scaleX,placement.scaleY);
            entry.sprite.zIndex=placement.depth;
          }
        }
        entry.sprite.visible = true;
      }

      if (!hasUsableSprite) {
        if (bt === 'decoration.signpost') {
          let fallback = this._signpostFallbacks.get(b.id);
          if (!fallback) {
            const gfx = new Graphics({ label: 'building-fallback:' + b.id });
            this.entityLayer.addChild(gfx);
            fallback = { gfx, lastSeen: performance.now() };
            this._signpostFallbacks.set(b.id, fallback);
          }
          fallback.lastSeen = performance.now();
          fallback.gfx.clear();
          fallback.gfx.zIndex = by;
          fallback.gfx.visible = true;
          this._drawSignpostGraphic(fallback.gfx, bx, by, 28 * scale);
        }
        continue;
      }

      const thIcon = spriteTexture!.height ?? 0;
      const iconYBase = thIcon > 0 ? by - thIcon * (buildingGroundAnchorY(spriteKey!) - 0.1) : by - 48 * scale;

      if (bt === 'house.quest_giver') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase - rh * 0.05, '!', 0xffd700, scale);
      } else if (bt === 'house.merchant') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase + rh * 0.12, '$', 0x88dd88, scale);
      } else if (bt === 'house.armorer') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase, 'A', 0xaaccff, scale);
      } else if (bt === 'house.weapon_smith') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase + rh * 0.04, 'W', 0xffaa66, scale);
      } else if (bt === 'house.jeweler') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase - rh * 0.06, 'J', 0xdd88ff, scale);
      } else if (bt === 'house.bounty_hunter') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase - rh * 0.05, 'B', 0xffcc44, scale);
      } else if (bt === 'house.elder') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase - rh * 0.05, 'E', 0xeeddaa, scale);
      } else if (bt === 'house.healer') {
        this._drawBuildingIcon(iconGfx, bx, iconYBase - rh * 0.05, '+', 0xff6666, scale);
      }
    }
  }

  /** Draw a small icon circle above a building to indicate its purpose. */
  private _drawBuildingIcon(
    gfx: Graphics, cx: number, cy: number, _icon: string, color: number, scale: number,
  ): void {
    const r = 6 * scale;
    gfx.circle(cx, cy, r);
    gfx.fill({ color, alpha: 0.6 });
    gfx.stroke({ color: 0x000000, width: 1.2, alpha: 0.4 });
  }

  /** Vector fallback for editor-placed signposts when the PNG is not bundled yet. */
  private _drawSignpostGraphic(gfx: Graphics, cx: number, cy: number, drawW: number): void {
    const postH = drawW * 1.5;
    const postW = Math.max(2.5, drawW * 0.1);
    const boardW = drawW * 0.9;
    const boardH = drawW * 0.38;
    const boardTop = cy - postH + boardH * 0.15;

    gfx.rect(cx - postW / 2, cy - postH, postW, postH);
    gfx.fill({ color: 0x5c4033, alpha: 0.95 });

    gfx.rect(cx - boardW / 2, boardTop, boardW, boardH);
    gfx.fill({ color: 0x8b6914, alpha: 0.95 });
    gfx.stroke({ color: 0x3d2817, width: 1.4, alpha: 0.85 });

    gfx.moveTo(cx - boardW * 0.22, boardTop + boardH * 0.55);
    gfx.lineTo(cx + boardW * 0.22, boardTop + boardH * 0.55);
    gfx.stroke({ color: 0x3d2817, width: 1.1, alpha: 0.75 });
  }

  /**
   * Paved town square at the settlement center (under well / fountain and civic building).
   */
  private _drawTownPlaza(
    gfx: Graphics,
    tx: number,
    ty: number,
    groundW: number,
    groundH: number,
  ): void {
    const pw = groundW * 0.42;
    const ph = groundH * 0.42;
    gfx.ellipse(tx, ty, pw, ph);
    gfx.fill({ color: 0x7a7878, alpha: 0.55 });
    gfx.stroke({ color: 0x5a5858, width: 1.2, alpha: 0.45 });
    const step = Math.max(10, pw * 0.14);
    for (let dx = -pw + step * 0.3; dx < pw; dx += step) {
      for (let dy = -ph + step * 0.25; dy < ph; dy += step * 0.85) {
        if ((dx * dx) / (pw * pw) + (dy * dy) / (ph * ph) > 0.82) continue;
        const h = ((Math.floor(dx / step) * 31) ^ (Math.floor(dy / step) * 17)) & 1;
        gfx.rect(tx + dx - step * 0.08, ty + dy - step * 0.08, step * 0.45, step * 0.38);
        gfx.fill({ color: h ? 0x6a686e : 0x757278, alpha: 0.35 });
      }
    }
  }

  /**
   * Procedural building placement when server buildings are unavailable (sprites only).
   */
  private _drawProceduralBuildings(
    townCenterX: number,
    townCenterY: number,
    tx: number,
    ty: number,
    spread: number,
    size: string,
    townSeed: number,
    usedBuildingSprites: Set<string>,
    townId: number,
    townRadius: number,
  ): void {
    const placements = proceduralTownBuildings(size, townSeed, townRadius);
    const layoutSpread = Math.max(spread, townRadius * TILE_WIDTH * 0.4);

    for (let i = 0; i < placements.length; i++) {
      const placement = placements[i]!;
      const facadeKey = PROCEDURAL_TOWN_HOUSE_FACADE_KEYS[placement.variant]!;
      const poolKey = `town:${townId}:proc_house:${i}`;
      const screen = worldToScreen(townCenterX + placement.offsetX, townCenterY + placement.offsetY);
      this._placeBuildingLayerSprite(poolKey, facadeKey, screen.x, screen.y, usedBuildingSprites, placement.scale);
    }

    // One market point belongs on the street edge; avoid a repeated ring of stalls.
    const stallCount = size === 'L' || size === 'XL' ? 2 : 1;
    for (let si = 0; si < stallCount; si++) {
      const side = si === 0 ? -1 : 1;
      const sx = tx + side * layoutSpread * 0.2;
      const sy = ty + layoutSpread * 0.05;
      const stallHash = ((townSeed * 41 + si * 19) ^ (si * 0x9e3779b9)) >>> 0;
      const stallVariant = (stallHash & 0x7fffffff) / 0x7fffffff;
      const stallKey = buildingTypeToTextureKey('decoration.stall', stallVariant) ?? MARKET_STALL_TEXTURE_KEY;
      const poolKey = `town:${townId}:proc_stall:${si}`;
      this._placeBuildingLayerSprite(poolKey, stallKey, sx, sy, usedBuildingSprites,
        Math.max(0.22, Math.min(0.58, townRadius * 0.045)));
    }
  }

  private _acquireTownLabel(labelIdx: number): { label: Text; nextIdx: number } {
    let label: Text;
    if (labelIdx < this._townLabels.length) {
      label = this._townLabels[labelIdx]!;
    } else {
      if (this._townLabelPool.length > 0) {
        label = this._townLabelPool.pop()!;
      } else {
        label = new Text({
          text: '',
          style: new TextStyle({
            fontSize: 18,
            fontFamily: 'system-ui, sans-serif',
            fontWeight: 'bold',
            fill: 0xdaa520,
            stroke: { color: 0x000000, width: 4 },
            align: 'center',
          }),
        });
        label.anchor.set(0.5, 0.5);
      }
      this.labelLayer.addChild(label);
      this._townLabels.push(label);
    }
    return { label, nextIdx: labelIdx + 1 };
  }

  /**
   * Draw towns visible in the current viewport (vector base + labels; buildings are sprites only).
   */
  drawTowns(towns: readonly ReadonlyData<TownData>[], camera: CameraView, screenWidth: number, screenHeight: number): void {
    this._npcServiceFacings.clear();
    for (const town of towns) for (const [id,face] of Object.entries(town.npcPresentation?.serviceFacings ?? {})) this._npcServiceFacings.set(Number(id), face);
    const bounds = viewportHalfExtents(screenWidth, screenHeight, 1, camera.zoom ?? MAP_ZOOM);
    const fortificationViewport = { left: camera.finalX - bounds.halfW, right: camera.finalX + bounds.halfW,
      top: camera.finalY - bounds.halfH, bottom: camera.finalY + bounds.halfH };
    // Filter before texture lookup: distant towns must retire their references
    // instead of pinning the entire world artwork against the native byte cap.
    this._fortifications?.draw(towns.filter(town => fortificationsIntersectViewport(town, fortificationViewport)));
    this._ambientInhabitants?.draw(towns, performance.now(), { left: camera.finalX - bounds.halfW, right: camera.finalX + bounds.halfW,
      top: camera.finalY - bounds.halfH, bottom: camera.finalY + bounds.halfH }, this._npcServerNowMs);
    const gfx = this._townGfx;
    const iconGfx = this._townIconGfx;
    const purposeGfx = this._buildingPurposeGfx;
    if (!gfx || !iconGfx || !purposeGfx) return;
    const cx = camera.finalX;
    const cy = camera.finalY;
    const textureRevision = this._spriteRegistry.revision;
    const viewportKey = `${screenWidth}:${screenHeight}:${camera.zoom ?? MAP_ZOOM}`;
    if (viewportKey !== this._lastTownViewport) { this._lastTownViewport = viewportKey; this._townDrawDirty = true; }
    if (this._lastTownTextureRevision !== textureRevision) {
      this._authoredCobbleTexture?.destroy(true); this._authoredCobbleTexture = null;
    }
    if (this._lastTownsRef !== towns || this._lastTownCount !== towns.length || this._lastTownTextureRevision !== textureRevision) {
      this._townDrawDirty = true;
      this._lastTownsRef = towns;
      this._lastTownCount = towns.length;
      this._lastTownTextureRevision = textureRevision;
    }
    const now = performance.now();
    const dx = Math.abs(cx - this._lastTownCameraX);
    const dy = Math.abs(cy - this._lastTownCameraY);
    if (
      !this._townDrawDirty &&
      dx < TILE_WIDTH &&
      dy < TILE_HEIGHT
    ) {
      return;
    }
    this._townDrawDirty = false;
    this._townRebuilds++;
    this._lastTownCameraX = cx;
    this._lastTownCameraY = cy;
    this._releaseGraphicsTextureCache(gfx);
    this._releaseGraphicsTextureCache(iconGfx);
    this._releaseGraphicsTextureCache(purposeGfx);
    gfx.clear();
    this._authoredPlazaGfx?.clear();
    iconGfx.clear();
    purposeGfx.clear();
    for (const [id, fallback] of this._signpostFallbacks) {
      fallback.gfx.visible = false;
      if (now - fallback.lastSeen >= SPRITE_HIDDEN_EVICT_MS) {
        fallback.gfx.destroy();
        this._signpostFallbacks.delete(id);
      }
    }

    // Hide all existing labels first; we'll show visible ones below
    for (const lbl of this._townLabels) {
      lbl.visible = false;
    }

    const halfW = screenWidth / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_WIDTH * 10;
    const halfH = screenHeight / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_HEIGHT * 10;

    let labelIdx = 0;

    const usedBuildingSprites = this._usedBuildingSprites;
    usedBuildingSprites.clear();
    for (const town of towns) {
      // Convert town world position to screen space
      const townScreen = worldToScreen(town.centerX, town.centerY);

      // Viewport culling: skip towns whose center is far outside the view
      const centerCull =
        Math.abs(townScreen.x - cx) > halfW + town.radius * TILE_WIDTH ||
        Math.abs(townScreen.y - cy) > halfH + town.radius * TILE_HEIGHT;
      if (centerCull) {
        if (town.biome === 'road' && town.buildings && town.buildings.length > 0) {
          let anyBuildingVisible = false;
          for (const b of town.buildings) {
            const bs = worldToScreen(b.worldX, b.worldY);
            if (
              Math.abs(bs.x - cx) <= halfW + TILE_WIDTH * 4 &&
              Math.abs(bs.y - cy) <= halfH + TILE_HEIGHT * 4
            ) {
              anyBuildingVisible = true;
              break;
            }
          }
          if (!anyBuildingVisible) {
            continue;
          }
        } else {
          continue;
        }
      }

      const tx = townScreen.x;
      const ty = townScreen.y;
      const s = Math.max(0.7, Math.min(1.6, town.radius / 8));

      if (town.authored) {
        this._drawAuthoredTownGround(gfx,town);
        this._drawServerBuildings(purposeGfx,usedBuildingSprites,town.buildings??[],s);
        const acquired=this._acquireTownLabel(labelIdx);
        acquired.label.text=town.name;acquired.label.x=tx;acquired.label.y=ty-140;
        acquired.label.visible=true;acquired.label.zIndex=10;labelIdx=acquired.nextIdx;
        continue;
      }

      // Roadside POIs: plaza at center + buildings; spur/cross-arm use procedural road tiles via roadNetwork.
      if (town.biome === 'road') {
        const groundW = town.radius * TILE_WIDTH * 0.55;
        const groundH = groundW * 0.5;
        if (this._spriteRegistry.manifest?.worldPresentation?.coherentTerrain !== true) {
          gfx.ellipse(tx, ty, groundW, groundH);
          gfx.fill({ color: 0x8a7454, alpha: 0.32 });
          gfx.ellipse(tx, ty, groundW * 0.55, groundH * 0.55);
          gfx.fill({ color: 0xa89272, alpha: 0.38 });
        }
        if (town.buildings && town.buildings.length > 0) {
          this._drawServerBuildings(purposeGfx, usedBuildingSprites, town.buildings, s);
        }
        const roadSpread = 100 * s;
        const { label: roadLabel, nextIdx: roadLabelIdx } = this._acquireTownLabel(labelIdx);
        roadLabel.text = town.name;
        roadLabel.x = tx;
        roadLabel.y = ty - roadSpread - 30 * s;
        roadLabel.visible = true;
        roadLabel.zIndex = 10;
        labelIdx = roadLabelIdx;
        continue;
      }

      if (this._spriteRegistry.manifest?.worldPresentation?.coherentTerrain !== true) {
        // --- Much larger border ---
        const borderRadius = town.radius * TILE_WIDTH * 0.7;
        const segments = 48;
        for (let i = 0; i < segments; i++) {
          const a1 = (i / segments) * Math.PI * 2;
          const a2 = ((i + 1) / segments) * Math.PI * 2;
          if (i % 2 === 0) {
            gfx.moveTo(tx + Math.cos(a1) * borderRadius, ty + Math.sin(a1) * borderRadius * 0.5);
            gfx.lineTo(tx + Math.cos(a2) * borderRadius, ty + Math.sin(a2) * borderRadius * 0.5);
            gfx.stroke({ color: 0xdaa520, width: 2, alpha: 0.3 });
          }
        }

        // --- Ground plane: tan/brown dirt ellipse (no extra transparent radius rings) ---
        const groundW = borderRadius * 0.85;
        const groundH = groundW * 0.5;
        gfx.ellipse(tx, ty, groundW, groundH);
        gfx.fill({ color: 0x8a7454, alpha: 0.35 });

        // --- Central plaza (paving); well/fountain + civic sit on or beside it ---
        this._drawTownPlaza(gfx, tx, ty, groundW, groundH);
      }

      const townSeed = typeof town.id === 'number' ? town.id : 0;
      const spread = 100 * s;

      // --- Buildings: server-driven if available, fallback procedural ---
      if (town.buildings && town.buildings.length > 0) {
        this._drawServerBuildings(purposeGfx, usedBuildingSprites, town.buildings, s);
      } else {
        this._drawProceduralBuildings(
          town.centerX, town.centerY, tx, ty, spread, town.size, townSeed, usedBuildingSprites, town.id, town.radius,
        );
        const plazaKey = `town:${town.id}:plaza:center`;
        this._placeBuildingLayerSprite(
          plazaKey,
          PLAZA_FOUNTAIN_TEXTURE_KEY,
          tx,
          ty,
          usedBuildingSprites,
          Math.max(0.35, Math.min(0.6, town.radius * 0.045)),
        );
        const civicWx = town.centerX + 0.18 * town.radius;
        const civicWy = town.centerY - 0.36 * town.radius;
        const civicScreen = worldToScreen(civicWx, civicWy);
        const civicKey = `town:${town.id}:civic`;
        this._placeBuildingLayerSprite(
          civicKey,
          TOWN_HALL_TEXTURE_KEY,
          civicScreen.x,
          civicScreen.y,
          usedBuildingSprites,
          Math.max(0.35, Math.min(0.6, town.radius * 0.045)),
        );
      }

      // --- Town name label (larger font, positioned higher) ---
      const { label, nextIdx } = this._acquireTownLabel(labelIdx);
      label.text = town.name;
      label.x = tx;
      label.y = ty - spread - 30 * s;
      label.visible = true;
      label.zIndex = 10;
      labelIdx = nextIdx;
    }

    this._hideUnusedSprites(this._buildingSpritePool, usedBuildingSprites, now);
    // Do not cacheAsTexture: town polylines sit at large world-screen coords and bake empty.
  }

  private _drawAuthoredTownGround(gfx: Graphics,town: ReadonlyData<TownData>): void {
    if (!town.authored) return;
    const paving = authoredPavingMaterial(town.authored.pavingArtFamily);
    const materialTexture = paving ? this._spriteRegistry.getTexture(paving.textureKey) : null;
    if (materialTexture) materialTexture.source.style.addressMode = 'mirror-repeat';
    if(!paving && !this._authoredCobbleTexture) {
      const tile=this._spriteRegistry.getTexture('terrain.plaza.v1');
      if(tile)this._authoredCobbleTexture=makeIsoRepeatTexture(tile);
    }
    const fillPolygon=(points: readonly {readonly x:number;readonly y:number}[],dirt:boolean,target=gfx,materialScale=1) => {
      if (points.length < 3) return;
      const coordinates=points.flatMap(p=>{const q=worldToScreen(p.x,p.y);return[q.x,q.y];});
      const texture=dirt?this._spriteRegistry.getTexture('terrain.road.pattern'):paving?materialTexture:this._authoredCobbleTexture;
      target.poly(coordinates).fill({color:dirt?0x91704e:paving?.color??0x8e8774});
      if(texture) {
        if (paving && !dirt) {
          target.poly(coordinates).fill({texture,textureSpace:'global',matrix:authoredPavingMatrix(points,texture.width,texture.height,paving.sourceSpanWU),color:0xffffff});
        } else {
          // Legacy diamond repeats retain their accepted scale and global phase.
          const repeatWidth=texture.width*materialScale,repeatHeight=texture.height*materialScale;
          const x = Math.floor(Math.min(...coordinates.filter((_,i)=>i%2===0))/repeatWidth)*repeatWidth;
          const y = Math.floor(Math.min(...coordinates.filter((_,i)=>i%2===1))/repeatHeight)*repeatHeight;
          target.poly(coordinates).fill({texture,textureSpace:'global',matrix:new Matrix().scale(materialScale,materialScale).translate(x,y),color:0xffffff});
        }
      }
    };
    const isDirt=(street:{paving?:'cobble'|'dirt';kind:string})=>!paving && (street.paving?street.paving==='dirt':town.authored!.kind==='village'||street.kind==='arterial'||street.kind==='approach');
    for(const street of [...town.authored.streets].sort((a,b)=>paving||a.paving||b.paving?Number(isDirt(b))-Number(isDirt(a)):Number(a.kind==='arterial')-Number(b.kind==='arterial'))) {
      const dirt=isDirt(street),half=street.width/2,target=paving||street.paving==='cobble'?(this._authoredPlazaGfx??gfx):gfx;
      for(let i=1;i<street.points.length;i++) {
        const a=street.points[i-1]!,b=street.points[i]!,length=Math.hypot(b.x-a.x,b.y-a.y);if(!length)continue;
        const dx=-(b.y-a.y)/length*half,dy=(b.x-a.x)/length*half;
        fillPolygon([{x:a.x+dx,y:a.y+dy},{x:b.x+dx,y:b.y+dy},{x:b.x-dx,y:b.y-dy},{x:a.x-dx,y:a.y-dy}],dirt,target);
      }
      for(const p of street.points)fillPolygon([{x:p.x-half,y:p.y-half},{x:p.x+half,y:p.y-half},{x:p.x+half,y:p.y+half},{x:p.x-half,y:p.y+half}],dirt,target);
    }
    // Biome material uses the same world scale on streets, squares and doors.
    for (const plaza of town.authored.plazas) {
      if (!plaza.material) {
        fillPolygon(plaza.polygon,false,this._authoredPlazaGfx??gfx,1.5);
        continue;
      }
      const target = this._authoredPlazaGfx ?? gfx;
      const coordinates = plaza.polygon.flatMap(p => { const q = worldToScreen(p.x,p.y); return [q.x,q.y]; });
      const earth = plaza.material === 'compacted_earth';
      const gravel = plaza.material === 'winter_gravel';
      target.poly(coordinates).fill({color: earth ? 0x98734f : gravel ? 0x858581 : 0x686d43});
      const texture = this._spriteRegistry.getTexture(earth ? 'terrain.compacted-earth.v1' : gravel ? 'terrain.winter-gravel.v1' : 'terrain.grass.pattern');
      if (texture) {
        texture.source.style.addressMode = 'mirror-repeat';
        target.poly(coordinates).fill({texture, textureSpace:'global', matrix:authoredPavingMatrix(plaza.polygon,texture.width,texture.height,4), color:0xffffff});
      }
    }
    const fenceGraphics = this._authoredPlazaGfx ?? gfx;
    for (const fence of town.authored.fences ?? []) {
      const a = worldToScreen(fence.from.x,fence.from.y), b = worldToScreen(fence.to.x,fence.to.y);
      for (const height of [19,36]) {
        fenceGraphics.moveTo(a.x,a.y-height).lineTo(b.x,b.y-height).stroke({color:0x65513c,width:3});
        fenceGraphics.moveTo(a.x,a.y-height-1).lineTo(b.x,b.y-height-1).stroke({color:0xa38a65,width:1});
      }
    }
  }

  async preloadAmbientInhabitants(towns: readonly ReadonlyData<TownData>[]): Promise<void> {
    await this._ambientInhabitants?.preload(towns, this._npcServerNowMs);
  }

  /**
   * Admin town editor: draw one settlement/town every frame (no throttle/cull).
   * Building and NPC sprites sit on entityLayer above plaza vector art.
   */
  drawEditorSettlement(town: TownData, now = performance.now()): void {
    this._fortifications?.draw([town]);
    this._ambientInhabitants?.draw([town], now, undefined, this._npcPresentationClock.sample(now));
    const gfx = this._townGfx;
    const iconGfx = this._townIconGfx;
    const purposeGfx = this._buildingPurposeGfx;
    if (!gfx || !iconGfx || !purposeGfx) return;

    this._releaseGraphicsTextureCache(gfx);
    this._releaseGraphicsTextureCache(iconGfx);
    this._releaseGraphicsTextureCache(purposeGfx);
    gfx.clear();
    this._authoredPlazaGfx?.clear();
    iconGfx.clear();
    purposeGfx.clear();
    for (const [id, fallback] of this._signpostFallbacks) {
      fallback.gfx.visible = false;
      if (now - fallback.lastSeen >= SPRITE_HIDDEN_EVICT_MS) {
        fallback.gfx.destroy();
        this._signpostFallbacks.delete(id);
      }
    }

    for (const lbl of this._townLabels) {
      lbl.visible = false;
    }

    const townScreen = worldToScreen(town.centerX, town.centerY);
    const tx = townScreen.x;
    const ty = townScreen.y;
    const s = Math.max(0.7, Math.min(1.6, town.radius / 8));

    const usedBuildingSprites = this._usedBuildingSprites;
    usedBuildingSprites.clear();

    if (town.authored) {
      this._drawAuthoredTownGround(gfx, town);
    } else if (this._spriteRegistry.manifest?.worldPresentation?.coherentTerrain !== true) {
      if (town.biome === 'road') {
        const groundW = town.radius * TILE_WIDTH * 0.55;
        const groundH = groundW * 0.5;
        gfx.ellipse(tx, ty, groundW, groundH);
        gfx.fill({ color: 0x8a7454, alpha: 0.32 });
        gfx.ellipse(tx, ty, groundW * 0.55, groundH * 0.55);
        gfx.fill({ color: 0xa89272, alpha: 0.38 });
      } else {
        const borderRadius = town.radius * TILE_WIDTH * 0.7;
        const segments = 48;
        for (let i = 0; i < segments; i++) {
          const a1 = (i / segments) * Math.PI * 2;
          const a2 = ((i + 1) / segments) * Math.PI * 2;
          if (i % 2 === 0) {
            gfx.moveTo(tx + Math.cos(a1) * borderRadius, ty + Math.sin(a1) * borderRadius * 0.5);
            gfx.lineTo(tx + Math.cos(a2) * borderRadius, ty + Math.sin(a2) * borderRadius * 0.5);
            gfx.stroke({ color: 0xdaa520, width: 2, alpha: 0.3 });
          }
        }
        const groundW = borderRadius * 0.85;
        const groundH = groundW * 0.5;
        gfx.ellipse(tx, ty, groundW, groundH);
        gfx.fill({ color: 0x8a7454, alpha: 0.35 });
        this._drawTownPlaza(gfx, tx, ty, groundW, groundH);
      }

    }
    if (town.buildings && town.buildings.length > 0) {
      this._drawServerBuildings(purposeGfx, usedBuildingSprites, town.buildings, s);
    } else if (!town.authored && town.biome !== 'road') {
      const townSeed = typeof town.id === 'number' ? town.id : 0;
      this._drawProceduralBuildings(
        town.centerX,
        town.centerY,
        tx,
        ty,
        100 * s,
        town.size,
        townSeed,
        usedBuildingSprites,
        town.id,
        town.radius,
      );
    }

    if (town.biome === 'road') {
      const roadSpread = 100 * s;
      const { label: roadLabel } = this._acquireTownLabel(0);
      roadLabel.text = town.name;
      roadLabel.x = tx;
      roadLabel.y = ty - roadSpread - 30 * s;
      roadLabel.visible = true;
      roadLabel.zIndex = 10;
    }

    this._hideUnusedSprites(this._buildingSpritePool, usedBuildingSprites, now);
    this.sortEntities(true);
  }

  /** Draw NPCs within the viewport (sprites + name labels; shadow is a small vector ellipse). */
  drawNPCs(
    npcs: readonly ReadonlyData<NPCData>[],
    camera: CameraView,
    screenWidth: number,
    screenHeight: number,
    now: number,
    options?: { editorMode?: boolean },
  ): void {
    const gfx = this._npcGfx;
    if (!gfx) return;
    gfx.clear();

    for (const lbl of this._npcLabels) {
      lbl.visible = false;
    }

    const camX = camera.finalX;
    const camY = camera.finalY;
    const pad = options?.editorMode ? 10 : 3;
    const halfW = screenWidth / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_WIDTH * pad;
    const halfH = screenHeight / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_HEIGHT * pad;

    let labelIdx = 0;
    const usedNpcSprites = this._usedNpcSprites;
    usedNpcSprites.clear();

    for (const npc of npcs) {
      const iso = worldToScreen(npc.worldX, npc.worldY);

      if (
        Math.abs(iso.x - camX) > halfW ||
        Math.abs(iso.y - camY) > halfH
      ) continue;

      const cx = iso.x;
      const cy = iso.y;

      const model = serviceNpcModel(npc);
      if (!model) continue;
      const actorKey = `npc:${npc.id}:${npc.townId}`;
      const animationKey = `${actorKey}:${model}`;
      const face = npc.presentationMotion ?? npc.presentationFacing ?? this._npcServiceFacings.get(npc.id);
      const facing = npcWorldFacing(face?.dx ?? 0, face?.dy ?? 0);
      let animation = this._serviceNpcAnimations.get(animationKey);
      if (!animation) { animation = new ServiceNpcAnimation(actorKey, model); this._serviceNpcAnimations.set(animationKey, animation); }
      const view = npc.presentationMotion
        ? { clip: npc.presentationMotion.clip === 'walk' ? 'walk' as const : 'catwalk' as const, elapsedMs: npc.presentationMotion.elapsedMs }
        : npc.isMoving ? { clip: 'walk' as const, elapsedMs: now }
        : animation.sample(now, facing, candidate => !!this._meshyNpcAssets.get(model, candidate, facing));
      const clip = view.clip;
      const definition = meshyNpcClip(model, clip, facing);
      const frames = this._meshyNpcAssets.get(model, clip, facing);
      const idleTexture = frames?.[meshyNpcFrame(definition, view.elapsedMs)] ?? null;
      const poolKey = `npc:${npc.id}`;
      const previous = this._npcSpritePool.get(poolKey)?.sprite.texture;
      const npcTexture = idleTexture ?? (previous && !previous.destroyed && previous !== Texture.EMPTY ? previous : null);
      if (!npcTexture) continue;

      gfx.ellipse(cx, cy + 8, 10, 3.5);
      gfx.fill({ color: 0x000000, alpha: 0.22 });

      usedNpcSprites.add(poolKey);
      const entry = this._ensureSprite(
        this._npcSpritePool,
        poolKey,
        `npc.meshy.${model}.${clip}.${facing}`,
        npcTexture,
        this.entityLayer,
      );
      entry.sprite.x = cx;
      entry.sprite.y = cy;
      entry.sprite.texture = npcTexture;
      const anchor = meshyNpcAnchor(definition);
      entry.sprite.anchor.set(anchor.x, anchor.y);
      entry.sprite.scale.set(1);
      entry.sprite.zIndex = iso.y;
      entry.sprite.visible = true;

      let nameLabel: Text;
      if (labelIdx < this._npcLabels.length) {
        nameLabel = this._npcLabels[labelIdx]!;
      } else {
        if (this._npcLabelPool.length > 0) {
          nameLabel = this._npcLabelPool.pop()!;
        } else {
          nameLabel = new Text({
            text: '',
            style: new TextStyle({
              fontSize: 10,
              fontFamily: 'system-ui, sans-serif',
              fill: 0xcccccc,
              stroke: { color: 0x000000, width: 2 },
              align: 'center',
            }),
          });
          nameLabel.anchor.set(0.5, 0.5);
        }
        this.labelLayer.addChild(nameLabel);
        this._npcLabels.push(nameLabel);
      }

      nameLabel.text = npc.name;
      nameLabel.x = cx;
      nameLabel.y = cy + 6;
      nameLabel.visible = true;
      nameLabel.zIndex = 20;
      labelIdx++;
    }

    this._hideUnusedSprites(this._npcSpritePool, usedNpcSprites, now);
    const presentActors = new Set(npcs.map(npc => `npc:${npc.id}:${npc.townId}:${serviceNpcModel(npc)}`));
    for (const key of this._serviceNpcAnimations.keys()) if (!presentActors.has(key)) this._serviceNpcAnimations.delete(key);
  }

  /**
   * Draw editor-defined town objects (props) within the viewport.
   */
  drawTownObjects(
    objects: readonly ReadonlyData<TownObjectData>[],
    camera: CameraView,
    screenWidth: number,
    screenHeight: number,
  ): void {
    const camX = camera.finalX;
    const camY = camera.finalY;
    const halfW = screenWidth / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_WIDTH * 3;
    const halfH = screenHeight / (2 * (camera.zoom ?? MAP_ZOOM)) + TILE_HEIGHT * 3;

    const usedTownObjects = this._usedTownObjectSprites;
    usedTownObjects.clear();

    for (const obj of objects) {
      const iso = worldToScreen(obj.worldX, obj.worldY);
      const variant = Number.isFinite(obj.variant) ? obj.variant : 0;
      const objTextureKey = obj.objectType==='authored_market_stall'?'prop.market.stall.v0'
        :obj.objectType==='authored_capital_fountain'?'prop.fountain.capital.v0'
        :obj.objectType==='authored_fountain'?'prop.fountain.plaza.v0'
        :obj.objectType==='authored_signpost'?'prop.signpost.v0'
        :this.getObjectTextureKey(obj.objectType, variant, obj.worldX, obj.worldY);
      // Shared metadata covers the full logical PNG, including shadows and frames.
      // Reject offscreen pixels before getTexture adds native source demand.
      if (objTextureKey && !this._authoritativePropIntersectsBounds(obj.worldX, obj.worldY, objTextureKey,
        { minX: camX - halfW, maxX: camX + halfW, minY: camY - halfH, maxY: camY + halfH })) continue;
      const objTexture = objTextureKey ? this._spriteRegistry.getTexture(objTextureKey) : null;
      if (objTextureKey && objTexture) {
        const p = spritePresentation(resolveManifestTexture(this._spriteRegistry.manifest, objTextureKey), objTexture.width, objTexture.height);
        if (!spriteIntersectsViewport(iso.x, iso.y, objTexture.width, objTexture.height,
          p?.scale ?? 1, p?.anchorX ?? 0.5, p?.anchorY ?? 1,
          camX - halfW, camX + halfW, camY - halfH, camY + halfH)) continue;
        const poolKey = `town-object:${obj.id}`;
        usedTownObjects.add(poolKey);
        const entry = this._ensureSprite(
          this._townObjectSpritePool,
          poolKey,
          objTextureKey,
          objTexture,
          this.entityLayer,
          obj.worldX,
          obj.worldY,
          this._townObjectSpriteFreeList,
        );
        entry.sprite.x = iso.x;
        entry.sprite.y = iso.y;
        entry.sprite.zIndex = obj.objectType === 'river_pond' ? -100_000 : iso.y;
        this._applyPresentation(entry, 1);
        entry.sprite.visible = true;
      }
    }

    this._hideUnusedSprites(
      this._townObjectSpritePool,
      usedTownObjects,
      performance.now(),
      this._townObjectSpriteFreeList,
      1200,
    );
  }

  /** Clear NPC visuals when there are none to render */
  clearNPCs(): void {
    if (this._npcGfx) this._npcGfx.clear();
    for (const lbl of this._npcLabels) {
      lbl.visible = false;
    }
    this._hideUnusedSprites(this._npcSpritePool, this._emptySpriteSet, performance.now());
  }

  /** Draw nearby heroes (sprites + labels; shadow is a small vector ellipse). */
  drawNearbyHeroes(
    heroes: ReadonlyArray<{
      id: number;
      name: string;
      level: number;
      modelVariant: number;
      positionX: number;
      positionY: number;
      isMoving?: boolean;
      motion?: HeroAnimationView;
    }>,
    camera: CameraView,
    screenWidth: number,
    screenHeight: number,
    now = performance.now(),
  ): void {
    const gfx = this._nearbyHeroGfx;
    if (!gfx) return;
    gfx.clear();

    // Hide all existing labels first
    for (const lbl of this._nearbyHeroLabels) {
      lbl.visible = false;
    }

    const camX = camera.finalX;
    const camY = camera.finalY;
    const { halfW, halfH } = viewportHalfExtents(screenWidth, screenHeight, 1.5, camera.zoom ?? MAP_ZOOM);

    let labelIdx = 0;
    const usedNearbySprites = this._usedNearbyHeroSprites;
    usedNearbySprites.clear();

    for (const hero of heroes) {
      const iso = worldToScreen(hero.positionX, hero.positionY);

      if (
        Math.abs(iso.x - camX) > halfW ||
        Math.abs(iso.y - camY) > halfH
      ) {
        continue;
      }

      const cx = iso.x;
      const cy = iso.y;

      const facing = hero.motion?.facing ?? 'south';
      const clip = hero.motion?.clip ?? 'idle';
      const textureKey = heroTextureKey(hero.modelVariant ?? 0, facing);
      const staticTexture = this._spriteRegistry.getTexture(textureKey);
      const candidate = this._heroAnimationAssets.definition(hero.modelVariant ?? 0, clip, facing);
      const candidateFrames = candidate ? this._heroAnimationAssets.get(clip, facing, candidate, hero.modelVariant ?? 0) : null;
      const idleCharacter = stableHeroIdleCharacter(hero.modelVariant);
      const idle = !candidate && idleCharacter && individualIdleFrame(now, `hero:${hero.id}`, idleCharacter, hero.isMoving === true);
      const frames = idle && this._individualIdleAssets.get(idle.character.character, idle.clip);
      const candidateTexture = candidateFrames && candidate
        ? candidateFrames[meshyClipFrame(candidate, hero.motion?.progress ?? 0)] : undefined;
      const texture = candidateTexture ?? (idle && frames
        ? frames[individualIdleTextureIndex(idle.character, idle.clip, idle.frame)] ?? staticTexture
        : staticTexture);
      if (!texture) continue;

      gfx.ellipse(cx, cy + 8, 10, 3);
      gfx.fill({ color: 0x000000, alpha: 0.2 });

      const poolKey = `nearby_hero:${hero.id}`;
      usedNearbySprites.add(poolKey);
      const entry = this._ensureSprite(
        this._nearbyHeroSpritePool,
        poolKey,
        textureKey,
        texture,
        this.entityLayer,
      );
      entry.sprite.x = cx;
      entry.sprite.y = cy;
      entry.sprite.texture = texture;
      if (candidateTexture && candidate) {
        const p = spritePresentation({ file: candidate.file, kind: 'character', presentation: candidate.presentation }, texture.width, texture.height)!;
        entry.sprite.anchor.set(p.anchorX, p.anchorY); entry.sprite.scale.set(p.scale);
      } else if (idle && texture !== staticTexture) { entry.sprite.anchor.set(0.5, 0.86); entry.sprite.scale.set(1); }
      else this._applyPresentation(entry, characterSpriteScale(texture.height));
      entry.sprite.zIndex = iso.y;
      entry.sprite.visible = true;

      // Label: "Name Lv.X"
      let label: Text;
      if (labelIdx < this._nearbyHeroLabels.length) {
        label = this._nearbyHeroLabels[labelIdx]!;
      } else {
        if (this._nearbyHeroLabelPool.length > 0) {
          label = this._nearbyHeroLabelPool.pop()!;
        } else {
          label = new Text({
            text: '',
            style: new TextStyle({
              fontSize: 9,
              fontFamily: 'system-ui, sans-serif',
              fill: 0x88ddaa,
              stroke: { color: 0x000000, width: 2 },
              align: 'center',
            }),
          });
          label.anchor.set(0.5, 0.5);
        }
        this.labelLayer.addChild(label);
        this._nearbyHeroLabels.push(label);
      }

      label.text = `${hero.name} Lv.${hero.level}`;
      label.x = cx;
      label.y = cy - 90;
      label.visible = true;
      label.zIndex = 20;
      labelIdx++;

    }

    this._hideUnusedSprites(this._nearbyHeroSpritePool, usedNearbySprites, performance.now());
  }

  /**
   * Up to two speech bubbles for hero meet. Local player's typed line: tinted fill; all lines use dark text.
   */
  drawHeroMeetBubbles(
    items: ReadonlyArray<{
      wx: number;
      wy: number;
      text: string;
      startMs: number;
      ownPlayerMessage: boolean;
    }>,
    now: number,
  ): void {
    for (let i = 0; i < this._meetBubbleSlots.length; i++) {
      const slot = this._meetBubbleSlots[i]!;
      const item = items[i];
      const gfx = slot.gfx;
      const txt = slot.txt;
      if (!item || !item.text.trim()) {
        gfx.clear();
        txt.visible = false;
        continue;
      }
      const elapsed = now - item.startMs;
      const alpha = Math.min(1, elapsed / 200);
      if (alpha <= 0) {
        txt.visible = false;
        gfx.clear();
        continue;
      }
      const iso = worldToScreen(item.wx, item.wy);
      const bx = iso.x;
      // Above hero name label (name sits at iso.y - CHARACTER_DISPLAY_HEIGHT_PX - 2).
      const by = iso.y - CHARACTER_DISPLAY_HEIGHT_PX - 38;
      txt.text = item.text;
      txt.style.fill = 0x1a1a1a;
      txt.style.stroke = { color: 0x000000, width: 0 };
      const padX = 8;
      const padY = 6;
      const w = Math.min(220, Math.max(72, Math.ceil(txt.width) + padX * 2));
      const h = Math.max(26, Math.ceil(txt.height) + padY * 2);
      const left = bx - w / 2;
      const top = by - h / 2;
      const fillRgb = item.ownPlayerMessage ? 0xcffafe : 0xffffff;
      const strokeRgb = item.ownPlayerMessage ? 0x67e8f9 : 0xcccccc;
      gfx.clear();
      gfx.roundRect(left, top, w, h, 6);
      gfx.fill({ color: fillRgb, alpha: 0.94 * alpha });
      gfx.stroke({ color: strokeRgb, width: 1, alpha: 0.8 * alpha });
      const triW = 7;
      const triH = 5;
      gfx.poly([bx - triW / 2, top + h, bx + triW / 2, top + h, bx, top + h + triH]);
      gfx.fill({ color: fillRgb, alpha: 0.94 * alpha });
      txt.x = bx;
      txt.y = by;
      txt.alpha = alpha;
      txt.visible = true;
      txt.zIndex = 31;
      gfx.zIndex = 30;
    }
  }

  clearHeroMeetBubbles(): void {
    this.drawHeroMeetBubbles([], performance.now());
  }

  /** Clear nearby hero visuals when there are none to render */
  clearNearbyHeroes(): void {
    if (this._nearbyHeroGfx) this._nearbyHeroGfx.clear();
    for (const lbl of this._nearbyHeroLabels) {
      lbl.visible = false;
    }
    this._hideUnusedSprites(this._nearbyHeroSpritePool, this._emptySpriteSet, performance.now());
  }

  private _visibleRestCamp(): SpritePoolEntry[] {
    if (!['rest_camp_tent', 'rest_camp_fire', 'rest_camp_bag'].every(key=>{
      const sprite=this._characterSpritePool.get(key)?.sprite;
      return sprite?.visible && sprite.alpha > 0 && !sprite.destroyed;
    })) return [];
    return ['rest_camp_tent', 'rest_camp_fire', 'rest_camp_bag', 'rest_camp_seat']
      .flatMap(key => { const entry = this._characterSpritePool.get(key); return entry?.sprite.visible ? [entry] : []; });
  }

  /** Sort world sprites by ground contact; Pixi also invokes this before rendering dirty children. */
  sortEntities(force = false): void {
    if (this._contextLost) return;
    this._drawFarProps();
    if (force) this.entityLayer.sortDirty = true;
    this.entityLayer.sortChildren();
    const scale = this.worldContainer.scale.x || 1;
    const left = -this.worldContainer.x / scale;
    const top = -this.worldContainer.y / scale;
    const hero = this._characterSpritePool.get('hero');
    const actors = function* (npcs: Map<string, SpritePoolEntry>): IterableIterator<VisibilityEntry> {
      if (hero) yield hero;
      yield* npcs.values();
    };
    const fortifications = this._fortifications?.entries.values() ?? [];
    const occluders = function* (pools: Map<string, SpritePoolEntry>[]): IterableIterator<VisibilityEntry> {
      yield* fortifications;
      for (const pool of pools) yield* pool.values();
    };
    this._actorVisibility.update(actors(this._npcSpritePool),
      occluders([this._objectSpritePool, this._townObjectSpritePool, this._buildingSpritePool]),
      key => key.startsWith('hero.whole.') ? this._heroPresentation : resolveManifestTexture(this._spriteRegistry.manifest, key),
      { left, top, right: left + this.app.screen.width / scale, bottom: top + this.app.screen.height / scale }, hero?.sprite, this._visibleRestCamp());
  }

  /** The editor inspector must report the same biome artwork selected for the actual placed object. */
  getObjectTextureKey(objectType: string, variant: number, worldX: number, worldY: number): string | null {
    return biomeObjectTextureKey(objectToTextureKey(objectType, variant), objectType, variant,
      worldX, worldY, this._worldTerrainContext, this._spriteRegistry.manifest?.worldPresentation?.biomeObjects,
      key => resolveManifestTexture(this._spriteRegistry.manifest, key) !== undefined);
  }

  getTextureManifestEntry(key: string): GameTextureManifestEntry | null {
    return resolveManifestTexture(this._spriteRegistry.manifest, key) ?? null;
  }

  getSpriteTexture(key: string): Texture | null {
    return this._spriteRegistry.getTexture(key);
  }

  /** Draw selection highlight and anchor handles for the admin town editor. */
  drawEditorOverlay(items: EditorOverlayItem[]): void {
    const gfx = this._editorOverlayGfx;
    if (!gfx) return;
    gfx.clear();
    if (!items.length) return;

    const anchorHalf = EDITOR_OBJECT_ANCHOR_PX / 2;

    for (const item of items) {
      if (item.kind === 'center') {
        const iso = worldToScreen(item.worldX, item.worldY);
        const r = (item.radius ?? 4.5) * TILE_WIDTH * 0.35;
        gfx.ellipse(iso.x, iso.y, r, r * 0.5);
        gfx.stroke({ color: 0x66ccff, width: 2, alpha: 0.85 });
        gfx.circle(iso.x, iso.y, 4);
        gfx.fill({ color: 0x66ccff, alpha: 0.9 });
        continue;
      }
      if (item.kind !== 'object' && item.kind !== 'npc') continue;
      const iso = worldToScreen(item.worldX, item.worldY);
      const selected = item.selected;
      gfx.rect(iso.x - anchorHalf, iso.y - anchorHalf, EDITOR_OBJECT_ANCHOR_PX, EDITOR_OBJECT_ANCHOR_PX);
      gfx.fill({
        color: selected ? 0xffcc66 : 0xffaa44,
        alpha: selected ? 0.28 : 0.14,
      });
      gfx.rect(iso.x - anchorHalf, iso.y - anchorHalf, EDITOR_OBJECT_ANCHOR_PX, EDITOR_OBJECT_ANCHOR_PX);
      gfx.stroke({
        color: selected ? 0xffdd88 : 0xffbb55,
        width: selected ? 2 : 1,
        alpha: selected ? 0.95 : 0.6,
      });
      gfx.circle(iso.x, iso.y, 2);
      gfx.fill({ color: 0xffffff, alpha: selected ? 0.95 : 0.7 });
    }

    for (const item of items) {
      if (!item.selected || item.kind !== 'building') continue;

      if (item.footprint) {
        const r = item.footprint;
        const points = [[r.minX, r.minY], [r.maxX, r.minY], [r.maxX, r.maxY], [r.minX, r.maxY]]
          .flatMap(([x, y]) => { const p = worldToScreen(x!, y!); return [p.x, p.y]; });
        gfx.poly(points).fill({ color: 0x66ccff, alpha: 0.22 });
        gfx.poly(points).stroke({ color: 0x88eeff, width: 2.5, alpha: 0.95 });
        continue;
      }

      const iso = worldToScreen(item.worldX, item.worldY);
      const fw = (item.footprintW ?? 2.5) * TILE_WIDTH * 0.55;
      const fh = (item.footprintH ?? 2.0) * TILE_HEIGHT * 0.55;
      gfx.rect(iso.x - fw / 2, iso.y - fh / 2, fw, fh);
      gfx.fill({ color: 0x66ccff, alpha: 0.22 });
      gfx.rect(iso.x - fw / 2, iso.y - fh / 2, fw, fh);
      gfx.stroke({ color: 0x88eeff, width: 2.5, alpha: 0.95 });
    }
  }

  clearEditorOverlay(): void {
    this._editorOverlayGfx?.clear();
  }

  /** Draw the parent road polyline and spine anchor for roadside POI editing. */
  drawEditorRoadContext(
    waypoints: Array<{ x: number; y: number }> | null | undefined,
    spine: { x: number; y: number } | null | undefined,
    _geometry?: {
      entryX: number;
      entryY: number;
      tangentX: number;
      tangentY: number;
    } | null,
  ): void {
    const gfx = this._editorOverlayGfx;
    if (!gfx) return;
    if (!waypoints || waypoints.length < 2) return;

    for (let i = 0; i < waypoints.length; i++) {
      const p = worldToScreen(waypoints[i]!.x, waypoints[i]!.y);
      if (i === 0) gfx.moveTo(p.x, p.y);
      else gfx.lineTo(p.x, p.y);
    }
    gfx.stroke({ color: 0xc4a574, width: 4, alpha: 0.9 });

    if (spine) {
      const s = worldToScreen(spine.x, spine.y);
      gfx.circle(s.x, s.y, 6);
      gfx.fill({ color: 0x66ff88, alpha: 0.95 });
      gfx.circle(s.x, s.y, 6);
      gfx.stroke({ color: 0x113322, width: 2, alpha: 0.95 });
    }
  }

  /** Clean up the renderer */
  destroy(): void {
    this._destroyed = true;
    this._initialized = false;
    // Application.init cannot be aborted. Its completion owns any late renderer.
    if (!this._initializing || this._appInitialized) this._disposeResources();
  }

  private _disposeResources(): void {
    if (this._resourcesDisposed) return;
    this._resourcesDisposed = true;
    this._actorVisibility.dispose();
    this._clearBiomeBlendTextures();
    if (this._appInitialized) {
      this.app.canvas.removeEventListener('webglcontextlost', this._onContextLost);
      this.app.canvas.removeEventListener('webglcontextrestored', this._onContextRestored);
    }
    this._overviewChunks.clear(); this._sceneProps.clear();
    this._runtimeScenes?.clear(); this._runtimeScenes = null;
    this._presentationCache.clear(); this._farProps.clear();
    this._fortifications?.destroy();this._fortifications=null;
    this._ambientInhabitants?.destroy();this._ambientInhabitants=null;
    resetIndividualIdleAnimations();
    this._restActivityClock.reset();
    this._restActivityView = null;
    this._terrainCache.clear();
    this._terrainDisplayCache.clear();
    if (this._appInitialized) {
      // Assets caches textures across sessions: destroying one canvas must not
      // invalidate the replacement StrictMode session's shared textures.
      this.app.destroy(true, { children: true, texture: false });
    } else {
      // A rejected app init may have allocated a renderer before a plugin failed.
      this.app.renderer?.destroy(true);
      this.app.stage.destroy({ children: true });
    }
    this._spriteRegistry.dispose();
    this._heroAnimationAssets.dispose();
    this._restIdleAssets.dispose();
    this._individualIdleAssets.dispose();
    this._meshyNpcAssets.dispose();
    this._serviceNpcAnimations.clear();
    if (!this._roadRibbon.destroyed) this._roadRibbon.destroy();
    this._authoredCobbleTexture?.destroy(true);this._authoredCobbleTexture=null;
    for (const container of [this.worldContainer, this.uiContainer, this.groundLayer,
      this.entityLayer, this.effectLayer, this.labelLayer, this._groundSpriteLayer, this._overviewLayer]) {
      if (!container.destroyed) container.destroy({ children: true });
    }
  }
}
